import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Plus, Search, Car, Clock, Trash2, X, Calendar, FileText, AlertCircle, Mail, Phone, Package2, Link, GripVertical, Users, Check } from 'lucide-react';
import { Customer, getCustomer } from '../lib/customers';
import { getPersonInitials } from '../utils/personInitials';
import { CustomerVehicle } from '../lib/customers';
import { Service, ServiceCategory } from '../lib/services';
import { createProposal, updateProposal, Proposal } from '../lib/proposals';
import { useBusiness } from '../contexts/BusinessContext';
import { useAppSystem } from '../hooks/useAppSystem';
import { supabase } from '../lib/supabase';
import EditCustomerModal from './manage/EditCustomerModal';
import SelectCustomerModal from './manage/SelectCustomerModal';
import SelectServiceModal from './manage/SelectServiceModal';
import SelectVehicleModal from './manage/SelectVehicleModal';
import CreateVehicleModal from './CreateVehicleModal';
import SelectInventoryItemModal from './manage/SelectInventoryItemModal';
import { sendProposalLink } from '../lib/proposals';
import { useToast } from '../components/ui/use-toast';
import { useNavigate } from 'react-router-dom';
import { ProductPhotoService } from '../services/productPhotoService';
import {
  uploadProposalServicePhoto,
  listProposalServicePhotos,
  deleteProposalServicePhoto,
  groupPhotosByLineKey,
  proposalServicePhotoUrl,
  MAX_PHOTOS_PER_SERVICE,
  type ProposalServicePhoto,
} from '../services/proposalServicePhotoService';
import { useImageViewer } from './ImageViewerProvider';
import { Image as ImageIcon, Globe, Ruler } from 'lucide-react';
import GoogleMeasurementModal, { MeasurementResult } from './proposals/GoogleMeasurementModal';
import ProposalTemplatesModal from './ProposalTemplatesModal';
import { ProposalTemplate, ProposalTemplateData, createProposalTemplate, updateProposalTemplate } from '../lib/proposalTemplates';
import { findOpenLinkableOpportunityForCustomer } from '../lib/crm/opportunityLinking';
import { recordOpportunityActivity } from '../lib/opportunityActivities';
import { getFieldQuotePhotoGroups, fieldQuotePhotoUrl, type FieldQuotePhotoGroup } from '../services/fieldQuotePhotoService';
import { getAllBookingPhotos, getBookingPhotoUrl } from '../services/bookingPhotoService';
import { getBookingWorkflowPhotos, getWorkflowPhotoUrl } from '../services/workflowPhotoService';

type BookingSourcePhoto = { id: string; file_name: string; url: string };
import { useModalZIndex } from '../contexts/ModalStackContext';
import { registerAssistantSurface } from '../lib/assistant/surface';

interface ServiceOption {
  id: string;
  vehicleId: string;
  vehicleName: string;
  name: string;
  originalName: string;
  description: string;
  originalDescription: string;
  priceOptionId: string;
  price: number; // This is the original price (or computed total for per-unit/sqft)
  duration_minutes: number;
  isSelectable?: boolean;
  // Per-unit / per-sqft fields
  pricing_type?: 'flat' | 'per_unit' | 'per_sqft';
  unit_price?: number;
  duration_per_unit?: number;
  quantity?: number;
  /** Display-only compare-at / "was" price snapshotted from the chosen price option. */
  compare_at_price?: number | null;
  _durationOverridden?: boolean;
  // Linked measurement
  measurement_index?: number;
  measurement_title?: string;
  // Discount fields
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  discountAmount?: number;
  discountedPrice?: number;
  // Per-line photos: stable identity + already-saved photos + pending uploads.
  line_key?: string;
  servicePhotos?: ProposalServicePhoto[];
  pendingPhotos?: File[];
}

interface ProductOption {
  id: string;
  vehicleId: string;
  vehicleName: string;
  name: string;
  acquisition_cost: number;
  price: number;
  quantity: number;
  category_name: string;
  // Discount fields
  discountType?: 'percentage' | 'dollar';
  discountValue?: number;
  discountAmount?: number;
  discountedPrice?: number;
}

interface Offer {
  title: string;
  services: ServiceOption[];
  products?: ProductOption[];
  deposit_percentage?: number | null;
  save_card_on_deposit?: boolean | null;
  show_customer_address?: boolean | null;
}

interface CreateProposalModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (createdProposal: Proposal) => void;
  initialCustomer?: Customer | null;
  initialProjectId?: string | null;
  initialBookingId?: string | null;
  initialOpportunityId?: number | null;
  initialProposal?: Proposal | null;
  templateMode?: boolean;
  editTemplate?: ProposalTemplate | null;
  onTemplateSaved?: (template: ProposalTemplate) => void;
}

export default function CreateProposalModal({
  isOpen,
  onClose,
  onSuccess,
  initialCustomer,
  initialProjectId,
  initialBookingId,
  initialOpportunityId,
  initialProposal,
  templateMode,
  editTemplate,
  onTemplateSaved,
}: CreateProposalModalProps) {
  const { selectedBusiness } = useBusiness();
  const [businessTaxRates, setBusinessTaxRates] = useState<{ name: string; rate: number }[]>([]);

  useEffect(() => {
    if (selectedBusiness?.id) {
      supabase
        .from('tax_rates')
        .select('name, rate')
        .eq('business_id', selectedBusiness.id)
        .then(({ data }) => { if (data) setBusinessTaxRates(data); });
    }
  }, [selectedBusiness?.id]);

  const combinedBusinessRate = businessTaxRates.reduce((sum, r) => sum + r.rate, 0) / 100;

  // App system hooks
  const { coreApps, installedAddons } = useAppSystem();
  
  // Check if Inventory app is installed
  const isInventoryAppInstalled = coreApps.some(app => app.app?.slug === 'inventory') || 
                                  installedAddons.some(app => app.app?.slug === 'inventory');
  
  // Check if Projects app is installed
  const isProjectsAppInstalled = coreApps.some(app => app.app?.slug === 'projects') || 
                                 installedAddons.some(app => app.app?.slug === 'projects');
  
  // State for inventory settings
  const [showInventoryInBookings, setShowInventoryInBookings] = useState(true);
  
  // Load inventory settings
  useEffect(() => {
    const loadInventorySettings = async () => {
      if (!selectedBusiness?.id || !isInventoryAppInstalled) return;

      try {
        const { data, error } = await supabase
          .rpc('get_app_setting', {
            p_business_id: selectedBusiness.id,
            p_app_slug: 'inventory',
            p_setting_key: 'show_inventory_in_bookings',
            p_default_value: true
          });

        if (!error && data !== null) {
          setShowInventoryInBookings(data);
        }
      } catch (error) {
        console.error('Error loading inventory settings:', error);
      }
    };

    loadInventorySettings();

    // Listen for setting changes
    const handleInventorySettingChange = (event: CustomEvent) => {
      if (event.detail.key === 'show_inventory_in_bookings') {
        setShowInventoryInBookings(event.detail.value);
      }
    };

    window.addEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);

    return () => {
      window.removeEventListener('inventorySettingChanged', handleInventorySettingChange as EventListener);
    };
  }, [selectedBusiness?.id, isInventoryAppInstalled]);

  useEffect(() => {
    if (!selectedBusiness?.id) return;
    supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'proposals',
        p_setting_key: 'google_earth_measurements_enabled',
        p_default_value: false,
      })
      .then(({ data, error }) => {
        if (!error && data !== null) setGoogleEarthEnabled(data);
      });
  }, [selectedBusiness?.id]);
  
  const [isAnimating, setIsAnimating] = useState(false);
  const [title, setTitle] = useState('');
  const [expiryOption, setExpiryOption] = useState('30');
  const [customExpiryDate, setCustomExpiryDate] = useState('');
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [googleEarthEnabled, setGoogleEarthEnabled] = useState(false);
  const [measurements, setMeasurements] = useState<MeasurementResult[]>([]);
  const [isMeasurementModalOpen, setIsMeasurementModalOpen] = useState(false);
  const [editingMeasurementIndex, setEditingMeasurementIndex] = useState<number | null>(null);
  const [measurementPickerServiceIndex, setMeasurementPickerServiceIndex] = useState<number | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isSelectCustomerModalOpen, setIsSelectCustomerModalOpen] = useState(false);
  const [isEditCustomerModalOpen, setIsEditCustomerModalOpen] = useState(false);
  const [isSelectVehicleModalOpen, setIsSelectVehicleModalOpen] = useState(false);
  const [isSelectServiceModalOpen, setIsSelectServiceModalOpen] = useState(false);
  const [isOfferModalOpen, setIsOfferModalOpen] = useState(false);
  const [services, setServices] = useState<Service[]>([]);
  const [categories, setCategories] = useState<ServiceCategory[]>([]);
  const [selectedServices, setSelectedServices] = useState<ServiceOption[]>([]);
  const [serviceAdditionalTaxes, setServiceAdditionalTaxes] = useState<{ service_id: string; name: string; rate: number }[]>([]);

  useEffect(() => {
    const serviceIds = selectedServices.map(s => s.id);
    if (serviceIds.length === 0) { setServiceAdditionalTaxes([]); return; }
    supabase
      .from('service_additional_taxes')
      .select('service_id, name, rate')
      .in('service_id', serviceIds)
      .then(({ data }) => { if (data) setServiceAdditionalTaxes(data); });
  }, [selectedServices.map(s => s.id).join(',')]);

  const [currentVehicle, setCurrentVehicle] = useState<CustomerVehicle | null>(null);
  const [selectedPriceOptions, setSelectedPriceOptions] = useState<Record<string, string>>({});
  const [offers, setOffers] = useState<Offer[]>([]);
  // Live mirror of `offers` for the AI bridge: when the assistant batches
  // several offer calls in one round, React hasn't re-rendered between them,
  // so render-captured state would be stale and offers would clobber each
  // other. Handlers read and write this ref; renders keep it in sync.
  const offersLiveRef = useRef<Offer[]>(offers);
  offersLiveRef.current = offers;
  const commitOffers = (next: Offer[]) => {
    offersLiveRef.current = next;
    setOffers(next);
  };
  const [currentOffer, setCurrentOffer] = useState<Offer | null>(null);
  const [draggedOfferIndex, setDraggedOfferIndex] = useState<number | null>(null);
  const [offerTitle, setOfferTitle] = useState('');
  const [showCustomerAddress, setShowCustomerAddress] = useState(false);
  const [requireDeposit, setRequireDeposit] = useState(false);
  const [saveCardOnDeposit, setSaveCardOnDeposit] = useState(false);
  const [offerDepositPercentage, setOfferDepositPercentage] = useState<string>('');
  const [selectedDepositPercentage, setSelectedDepositPercentage] = useState<'25' | '50' | '75' | 'full' | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isCreateVehicleModalOpen, setIsCreateVehicleModalOpen] = useState(false);
  const [isSelectInventoryItemModalOpen, setIsSelectInventoryItemModalOpen] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState<ProductOption[]>([]);
  const [productPhotos, setProductPhotos] = useState<Record<string, string>>({});
  const offerModalScrollRef = useRef<HTMLDivElement>(null);
  const savedOfferScrollTopRef = useRef<number>(0);
  const didToggleDepositRef = useRef<boolean>(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkModalView, setLinkModalView] = useState<'options' | 'projects' | 'opportunities'>('options');
  const [projects, setProjects] = useState<Array<{ id: string; title: string; status: string; customer_id: string | null }>>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(false);
  const [projectSearchQuery, setProjectSearchQuery] = useState('');
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(initialProjectId || null);
  const [selectedProjectTitle, setSelectedProjectTitle] = useState<string | null>(null);
  const [selectedProjectCustomerId, setSelectedProjectCustomerId] = useState<string | null>(null);
  const [showCustomerConflictModal, setShowCustomerConflictModal] = useState(false);
  const [conflictProjectCustomerName, setConflictProjectCustomerName] = useState<string | null>(null);
  const [opportunities, setOpportunities] = useState<Array<{ id: number; title: string; customer_id: string | null }>>([]);
  const [isLoadingOpportunities, setIsLoadingOpportunities] = useState(false);
  const [opportunitySearchQuery, setOpportunitySearchQuery] = useState('');
  const [selectedOpportunityId, setSelectedOpportunityId] = useState<number | null>(initialOpportunityId || null);
  const [selectedOpportunityTitle, setSelectedOpportunityTitle] = useState<string | null>(null);
  const [selectedOpportunityCustomerId, setSelectedOpportunityCustomerId] = useState<string | null>(null);
  const userClearedOpportunityLinkRef = useRef(false);
  const [uploadingPhotos, setUploadingPhotos] = useState(false);
  // Cache-bust tokens (per saved photo id) so an edited image refetches instead
  // of showing the browser-cached copy at the same URL.
  const [photoBust, setPhotoBust] = useState<Record<string, number>>({});
  // Field-quote photos (grouped by field quote) from the linked lead and/or the
  // proposal's customer, offered as a source when adding photos to a service line.
  const [fieldQuotePhotoGroups, setFieldQuotePhotoGroups] = useState<FieldQuotePhotoGroup[]>([]);
  // Which service line a photo is being added to, and which picker is showing.
  const [addPhotoTarget, setAddPhotoTarget] = useState<{ offerIndex: number; serviceIndex: number } | null>(null);
  const [linkedPickerTarget, setLinkedPickerTarget] = useState<{ offerIndex: number; serviceIndex: number } | null>(null);
  const [linkedSelected, setLinkedSelected] = useState<Set<string>>(new Set());
  // Booking photos from the linked booking, offered as a source when the
  // proposal is created against a booking (sales-tool flow). Combines vehicle
  // inspection photos (booking_photos) and job/workflow photos (workflow_photos).
  const [bookingPhotos, setBookingPhotos] = useState<BookingSourcePhoto[]>([]);
  const [bookingPickerTarget, setBookingPickerTarget] = useState<{ offerIndex: number; serviceIndex: number } | null>(null);
  const [bookingSelected, setBookingSelected] = useState<Set<string>>(new Set());
  const addFileTargetRef = useRef<{ offerIndex: number; serviceIndex: number } | null>(null);
  const addFileInputRef = useRef<HTMLInputElement>(null);

  // Load field-quote photos from the linked lead and/or the customer so they can
  // be reused when building the proposal.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!selectedBusiness?.id || (!selectedOpportunityId && !selectedCustomer?.id)) {
        setFieldQuotePhotoGroups([]);
        return;
      }
      const groups = await getFieldQuotePhotoGroups(selectedBusiness.id, {
        customerId: selectedCustomer?.id,
        opportunityId: selectedOpportunityId,
      });
      if (!cancelled) setFieldQuotePhotoGroups(groups);
    })();
    return () => { cancelled = true; };
  }, [selectedBusiness?.id, selectedOpportunityId, selectedCustomer?.id]);

  // Load photos from the linked booking so they can be reused on service lines.
  // Pulls from both inspection photos and workflow/job photos.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!initialBookingId) {
        setBookingPhotos([]);
        return;
      }
      const [inspection, workflow] = await Promise.all([
        getAllBookingPhotos(initialBookingId).catch((e) => { console.error('[BookingPhotos] inspection error', e); return []; }),
        getBookingWorkflowPhotos(initialBookingId).catch((e) => { console.error('[BookingPhotos] workflow error', e); return []; }),
      ]);
      console.log('🧷 [BookingPhotos] load', {
        initialBookingId,
        inspectionCount: inspection.length,
        workflowCount: workflow.length,
      });
      const combined: BookingSourcePhoto[] = [
        ...inspection.map((p) => ({ id: p.id, file_name: p.file_name, url: getBookingPhotoUrl(p.file_path) })),
        ...workflow.map((p) => ({ id: p.id, file_name: p.file_name, url: getWorkflowPhotoUrl(p.file_path) })),
      ];
      if (!cancelled) setBookingPhotos(combined);
    })();
    return () => { cancelled = true; };
  }, [initialBookingId]);
  const { open: openImageViewer } = useImageViewer();

  // Template feature state
  const [isTemplatesModalOpen, setIsTemplatesModalOpen] = useState(false);
  const [isTemplateCreateOpen, setIsTemplateCreateOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<ProposalTemplate | null>(null);
  const [templateName, setTemplateName] = useState('');
  const [templatesRefreshKey, setTemplatesRefreshKey] = useState(0);
  const [appliedTemplateName, setAppliedTemplateName] = useState<string | null>(null);
  const [assignVehicleOfferIndex, setAssignVehicleOfferIndex] = useState<number | null>(null);
  const [isAssignVehicleModalOpen, setIsAssignVehicleModalOpen] = useState(false);

  // Fetch projects when Projects option is clicked
  const fetchProjects = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoadingProjects(true);
    try {
      const { data, error } = await supabase
        .from('projects_active')
        .select('id, title, status, customer_id, proposal_id')
        .eq('business_id', selectedBusiness.id)
        .is('proposal_id', null) // Only show projects without proposals linked
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching projects:', error);
        toast({
          title: 'Error',
          description: 'Failed to load projects',
          variant: 'destructive'
        });
        return;
      }

      setProjects(data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast({
        title: 'Error',
        description: 'Failed to load projects',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingProjects(false);
    }
  };

  // Filter projects based on search query
  const filteredProjects = projects.filter(project =>
    project.title.toLowerCase().includes(projectSearchQuery.toLowerCase())
  );

  // Fetch opportunities when Opportunities option is clicked
  const fetchOpportunities = async () => {
    if (!selectedBusiness?.id) return;

    setIsLoadingOpportunities(true);
    try {
      const { data, error } = await supabase
        .from('pipeline_opportunities')
        .select('id, title, customer_id')
        .eq('business_id', selectedBusiness.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching opportunities:', error);
        toast({
          title: 'Error',
          description: 'Failed to load opportunities',
          variant: 'destructive'
        });
        return;
      }

      setOpportunities(data || []);
    } catch (error) {
      console.error('Error fetching opportunities:', error);
      toast({
        title: 'Error',
        description: 'Failed to load opportunities',
        variant: 'destructive'
      });
    } finally {
      setIsLoadingOpportunities(false);
    }
  };

  // Filter opportunities based on search query
  const filteredOpportunities = opportunities.filter(opportunity =>
    opportunity.title.toLowerCase().includes(opportunitySearchQuery.toLowerCase())
  );

  const [isCustomerValid, setIsCustomerValid] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  // Resolve z-index from the modal stack so this modal (and its nested sub-modals) always render
  // above whatever opened it — e.g. the CustomerMessageModal (z ~100160). Falls back to the original
  // hardcoded values when opened standalone.
  const baseZ = useModalZIndex(templateMode ? 100170 : 100140);

  const tryAutoLinkOpportunityForCustomer = async (customer: Customer) => {
    if (
      templateMode ||
      initialProposal ||
      initialOpportunityId ||
      userClearedOpportunityLinkRef.current ||
      !selectedBusiness?.id ||
      !customer.id
    ) {
      return;
    }

    try {
      const linkableOpportunity = await findOpenLinkableOpportunityForCustomer(
        selectedBusiness.id,
        customer.id,
      );

      if (linkableOpportunity) {
        setSelectedOpportunityId(linkableOpportunity.id);
        setSelectedOpportunityTitle(linkableOpportunity.title);
        setSelectedOpportunityCustomerId(customer.id);
        toast({
          title: 'CRM opportunity linked',
          description: `Auto-linked to "${linkableOpportunity.title}"`,
        });
        return;
      }

      setSelectedOpportunityId(null);
      setSelectedOpportunityTitle(null);
      setSelectedOpportunityCustomerId(null);
    } catch (error) {
      console.error('Error auto-linking CRM opportunity for customer:', error);
    }
  };

  React.useEffect(() => {
    if (isOpen) {
      setIsAnimating(true);
      document.body.style.overflow = 'hidden';
      userClearedOpportunityLinkRef.current = false;
      setTitle(templateMode ? '' : 'Proposal');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
      if (templateMode) setTemplateName('');
    } else {
      document.body.style.overflow = 'unset';
      // Reset form when modal closes
      setTitle('');
      setExpiryOption('30');
      setCustomExpiryDate('');
      setShowCustomDate(false);
      setSelectedCustomer(null);
      setSelectedOpportunityId(null);
      setSelectedOpportunityTitle(null);
      setSelectedOpportunityCustomerId(null);
      setIsOfferModalOpen(false);
      setSelectedServices([]);
      setSelectedProducts([]);
      setProductPhotos({});
      setCurrentVehicle(null);
      setOffers([]);
      setCurrentOffer(null);
      setOfferTitle('');
      setError(null);
      setMeasurements([]);
      setAppliedTemplateName(null);
    }
    setShowValidationMessages(false);
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Initialize customer when modal opens
  useEffect(() => {
    const loadInitialCustomer = async () => {
      if (isOpen && initialCustomer) {
        console.log('📄 [CreateProposalModal] Pre-populating customer:', {
          customer: initialCustomer.first_name + ' ' + initialCustomer.last_name
        });
        // Fetch full customer with vehicles if customer ID is available
        if (initialCustomer.id) {
          try {
            const fullCustomer = await getCustomer(initialCustomer.id);
            setSelectedCustomer(fullCustomer);
            await tryAutoLinkOpportunityForCustomer(fullCustomer);
          } catch (error) {
            console.error('Error loading customer with vehicles:', error);
            setSelectedCustomer(initialCustomer);
            await tryAutoLinkOpportunityForCustomer(initialCustomer);
          }
        } else {
          setSelectedCustomer(initialCustomer);
        }
      }
    };
    
    loadInitialCustomer();
  }, [isOpen, initialCustomer]);

  // Load project when initialProjectId is provided
  useEffect(() => {
    const loadProject = async () => {
      if (initialProjectId && isOpen && selectedBusiness?.id) {
        try {
          const { data, error } = await supabase
            .from('projects_active')
            .select('id, title, customer_id')
            .eq('business_id', selectedBusiness.id)
            .eq('id', initialProjectId)
            .single();

          if (error) {
            console.error('Error loading project:', error);
            setSelectedProjectTitle(null);
          } else           if (data) {
            setSelectedProjectTitle(data.title);
            setSelectedProjectCustomerId(data.customer_id || null);
          }
        } catch (error) {
          console.error('Error loading project:', error);
          setSelectedProjectTitle(null);
          setSelectedProjectCustomerId(null);
        }
      } else if (!initialProjectId) {
        setSelectedProjectTitle(null);
        setSelectedProjectCustomerId(null);
      }
    };

    loadProject();
  }, [isOpen, initialProjectId, selectedBusiness?.id]);

  // Load opportunity when initialOpportunityId is provided
  useEffect(() => {
    const loadOpportunity = async () => {
      if (initialOpportunityId && isOpen && selectedBusiness?.id) {
        try {
          const { data, error } = await supabase
            .from('pipeline_opportunities')
            .select('id, title, customer_id')
            .eq('business_id', selectedBusiness.id)
            .eq('id', initialOpportunityId)
            .single();

          if (error) {
            console.error('Error loading opportunity:', error);
            setSelectedOpportunityTitle(null);
          } else if (data) {
            setSelectedOpportunityTitle(data.title);
            setSelectedOpportunityCustomerId(data.customer_id || null);
            
            // Pre-fill customer if not already set
            if (data.customer_id && !selectedCustomer) {
              try {
                const customer = await getCustomer(data.customer_id);
                setSelectedCustomer(customer);
              } catch (err) {
                console.error('Error loading customer from opportunity:', err);
              }
            }
          }
        } catch (error) {
          console.error('Error loading opportunity:', error);
          setSelectedOpportunityTitle(null);
          setSelectedOpportunityCustomerId(null);
        }
      }
      // NOTE: when there's no initialOpportunityId we intentionally do NOT clear the linked
      // opportunity here. Auto-linking (tryAutoLinkOpportunityForCustomer) sets the linked
      // opportunity for the current customer, and this effect re-runs whenever selectedCustomer
      // changes — clearing here would wipe the auto-linked result in a race. Clearing is handled
      // by the open/close reset effect and by the explicit "unlink" button instead.
    };

    loadOpportunity();
  }, [isOpen, initialOpportunityId, selectedBusiness?.id, selectedCustomer]);

  // Pre-fill form when editing an existing proposal
  useEffect(() => {
    const loadInitialProposal = async () => {
      if (isOpen && initialProposal) {
        console.log('📄 [CreateProposalModal] Pre-populating proposal:', initialProposal);
        
        // Set basic proposal fields
        setTitle(initialProposal.title || '');
        
        // Set expiry date
        if (initialProposal.valid_until) {
          const expiryDate = new Date(initialProposal.valid_until);
          const today = new Date();
          const diffDays = Math.ceil((expiryDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
          
          if (diffDays === 30) {
            setExpiryOption('30');
            setShowCustomDate(false);
          } else if (diffDays === 60) {
            setExpiryOption('60');
            setShowCustomDate(false);
          } else if (diffDays === 90) {
            setExpiryOption('90');
            setShowCustomDate(false);
          } else {
            setShowCustomDate(true);
            setCustomExpiryDate(initialProposal.valid_until.split('T')[0]);
          }
        }

        // Set customer if available
        if (initialProposal.customer && initialProposal.customer_id) {
          try {
            const fullCustomer = await getCustomer(initialProposal.customer_id);
            setSelectedCustomer(fullCustomer);
          } catch (error) {
            console.error('Error loading customer:', error);
            // Fallback to basic customer data
            if (initialProposal.customer) {
              setSelectedCustomer({
                id: initialProposal.customer_id,
                first_name: initialProposal.customer.first_name,
                last_name: initialProposal.customer.last_name || '',
                email: initialProposal.customer.email || '',
                phone: initialProposal.customer.phone || ''
              } as Customer);
            }
          }
        }

        // Set project
        if (initialProposal.project_id) {
          setSelectedProjectId(initialProposal.project_id);
        }

        // (Per-service photos are loaded below, attached to each service by line_key.)

        // Convert offers to form format
        if (initialProposal.offers && initialProposal.offers.length > 0) {
          const convertedOffers: Offer[] = await Promise.all(
            initialProposal.offers.map(async (offer) => {
              // Convert services
              const services: ServiceOption[] = (offer.services || []).map((service) => {
                const pt = (service as any).pricing_type || 'flat';
                const isPerUnit = pt === 'per_unit' || pt === 'per_sqft';
                return {
                  id: service.service_id,
                  vehicleId: service.vehicle_id || 'no-vehicle',
                  vehicleName: service.vehicle_make && service.vehicle_model
                    ? `${service.vehicle_year || ''} ${service.vehicle_make} ${service.vehicle_model}`.trim()
                    : 'No vehicle',
                  name: service.name,
                  originalName: service.name.replace(' (Optional)', ''),
                  description: service.description || '',
                  originalDescription: service.description || '',
                  priceOptionId: service.id,
                  price: service.price,
                  duration_minutes: service.duration_minutes,
                  isSelectable: service.isSelectable || false,
                  discountType: (service as any).discount_type,
                  discountValue: (service as any).discount_value,
                  discountAmount: service.discount_amount || 0,
                  discountedPrice: service.price - (service.discount_amount || 0),
                  pricing_type: pt,
                  unit_price: isPerUnit ? ((service as any).unit_price || 0) : undefined,
                  duration_per_unit: isPerUnit ? ((service as any).duration_per_unit || 0) : undefined,
                  quantity: isPerUnit ? ((service as any).quantity || 0) : undefined,
                  compare_at_price: (service as any).compare_at_price ?? null,
                  line_key: (service as any).line_key || undefined,
                  servicePhotos: [],
                  pendingPhotos: [],
                };
              });

              // Convert products
              const products: ProductOption[] = ((offer as any).products || []).map((product: any) => ({
                id: product.stock_item_id,
                vehicleId: product.vehicle_id || 'no-vehicle',
                vehicleName: product.vehicle_make && product.vehicle_model
                  ? `${product.vehicle_year || ''} ${product.vehicle_make} ${product.vehicle_model}`.trim()
                  : 'No vehicle',
                name: product.name,
                acquisition_cost: product.acquisition_cost || 0,
                price: product.price,
                quantity: product.quantity || 1,
                category_name: product.category_name || '',
                discountType: product.discount_type,
                discountValue: product.discount_value,
                discountAmount: product.discount_amount || 0,
                discountedPrice: product.price - (product.discount_amount || 0)
              }));

              return {
                title: offer.title,
                services,
                products,
                deposit_percentage: offer.deposit_percentage || null,
                save_card_on_deposit: !!(offer as { save_card_on_deposit?: boolean }).save_card_on_deposit,
                show_customer_address: !!(offer as { show_customer_address?: boolean }).show_customer_address,
              };
            })
          );

          // Attach existing per-service photos by line_key.
          try {
            if (initialProposal.id) {
              const allServicePhotos = await listProposalServicePhotos(initialProposal.id);
              const byKey = groupPhotosByLineKey(allServicePhotos);
              for (const off of convertedOffers) {
                for (const svc of off.services) {
                  if (svc.line_key && byKey[svc.line_key]) svc.servicePhotos = byKey[svc.line_key];
                }
              }
            }
          } catch (e) {
            console.error('Error loading per-service photos:', e);
          }

          setOffers(convertedOffers);
          // Don't show the new offer input area when editing - only show existing offers
          setIsOfferModalOpen(false);
          setCurrentOffer(null);
        }
      }
    };

    loadInitialProposal();
  }, [isOpen, initialProposal]);

  // Pre-fill from template (editing an existing template or applying a selected template)
  useEffect(() => {
    if (!isOpen || !editTemplate) return;
    const td = editTemplate.template_data;
    setTemplateName(editTemplate.name || '');
    if (td.title) setTitle(td.title);
    if (td.expiryOption) setExpiryOption(td.expiryOption);

    if (td.offers?.length) {
      const converted: Offer[] = td.offers.map(o => ({
        title: o.title,
        deposit_percentage: o.deposit_percentage ?? null,
        save_card_on_deposit: o.save_card_on_deposit ?? null,
        show_customer_address: o.show_customer_address ?? null,
        services: o.services.map(s => ({
          id: s.service_id,
          vehicleId: s.vehicle_id || 'no-vehicle',
          vehicleName: 'No vehicle',
          name: s.name,
          originalName: s.name.replace(' (Optional)', ''),
          description: s.description || '',
          originalDescription: s.description || '',
          priceOptionId: s.service_id,
          price: s.price,
          duration_minutes: s.duration_minutes,
          isSelectable: s.isSelectable || false,
          discountType: s.discountType,
          discountValue: s.discountValue,
          pricing_type: s.pricing_type || 'flat',
          unit_price: s.unit_price,
          duration_per_unit: s.duration_per_unit,
          quantity: s.quantity,
          compare_at_price: (s as any).compare_at_price ?? null,
        })),
        products: (o.products || []).map(p => ({
          id: p.stock_item_id,
          vehicleId: p.vehicle_id || 'no-vehicle',
          vehicleName: 'No vehicle',
          name: p.name,
          acquisition_cost: 0,
          price: p.price,
          quantity: p.quantity,
          category_name: p.category_name,
          discountType: p.discountType,
          discountValue: p.discountValue,
        })),
      }));
      setOffers(converted);
      setIsOfferModalOpen(false);
      setCurrentOffer(null);
    }
  }, [isOpen, editTemplate]);

  // Apply a selected template to the current proposal form
  const applyTemplate = (template: ProposalTemplate) => {
    const td = template.template_data;
    if (td.title) setTitle(td.title);
    if (td.expiryOption) setExpiryOption(td.expiryOption);

    if (td.offers?.length) {
      const converted: Offer[] = td.offers.map(o => ({
        title: o.title,
        deposit_percentage: o.deposit_percentage ?? null,
        save_card_on_deposit: o.save_card_on_deposit ?? null,
        show_customer_address: o.show_customer_address ?? null,
        services: o.services.map(s => ({
          id: s.service_id,
          vehicleId: s.vehicle_id || 'no-vehicle',
          vehicleName: 'No vehicle',
          name: s.name,
          originalName: s.name.replace(' (Optional)', ''),
          description: s.description || '',
          originalDescription: s.description || '',
          priceOptionId: s.service_id,
          price: s.price,
          duration_minutes: s.duration_minutes,
          isSelectable: s.isSelectable || false,
          discountType: s.discountType,
          discountValue: s.discountValue,
          pricing_type: s.pricing_type || 'flat',
          unit_price: s.unit_price,
          duration_per_unit: s.duration_per_unit,
          quantity: s.quantity,
          compare_at_price: (s as any).compare_at_price ?? null,
        })),
        products: (o.products || []).map(p => ({
          id: p.stock_item_id,
          vehicleId: p.vehicle_id || 'no-vehicle',
          vehicleName: 'No vehicle',
          name: p.name,
          acquisition_cost: 0,
          price: p.price,
          quantity: p.quantity,
          category_name: p.category_name,
          discountType: p.discountType,
          discountValue: p.discountValue,
        })),
      }));
      setOffers(converted);
      setIsOfferModalOpen(false);
      setCurrentOffer(null);
    }

    setAppliedTemplateName(template.name);
    toast({ title: 'Template applied', description: `"${template.name}" has been loaded.` });
  };

  // Add this useEffect right after the existing useEffect blocks to handle the selectable service flag
  useEffect(() => {
    // Check and clean up localStorage when modal opens or closes
    if (!isSelectServiceModalOpen) {
      localStorage.removeItem('adding_selectable_service');
    }
  }, [isSelectServiceModalOpen]);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleAddService = (service: Service, priceOption: any, isSelectable: boolean = false) => {
    // Check industry - for Home Services / template mode, vehicle is optional
    const isHomeServices = selectedBusiness?.industry === 'Home Services' || templateMode;
    
    if (!isHomeServices && !currentVehicle) return;

    const pricingType = priceOption.pricing_type || 'flat';
    const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';
    
    const newService: ServiceOption = {
      id: service.id,
      vehicleId: currentVehicle?.id || 'no-vehicle',
      vehicleName: currentVehicle 
        ? `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`
        : 'No vehicle',
      name: isSelectable ? `${service.name} (Optional)` : service.name,
      originalName: service.name,
      description: service.description || '',
      originalDescription: service.description || '',
      priceOptionId: priceOption.id,
      price: isPerUnit ? 0 : priceOption.price,
      duration_minutes: isPerUnit ? 0 : priceOption.duration_minutes,
      isSelectable: isSelectable,
      pricing_type: pricingType,
      unit_price: isPerUnit ? (priceOption.unit_price || 0) : undefined,
      duration_per_unit: isPerUnit ? (priceOption.duration_per_unit || 0) : undefined,
      quantity: isPerUnit ? 0 : undefined,
      compare_at_price: priceOption.compare_at_price ?? null
    };

    setSelectedServices(prev => [...prev, newService]);
    setIsSelectServiceModalOpen(false);
  };

  const handleAddProduct = async (product: any) => {
    // Check industry - for Home Services / template mode, vehicle is optional
    const isHomeServices = selectedBusiness?.industry === 'Home Services' || templateMode;
    
    if (!isHomeServices && !currentVehicle) return;
    
    const newProduct: ProductOption = {
      id: product.id,
      vehicleId: currentVehicle?.id || 'no-vehicle',
      vehicleName: currentVehicle 
        ? `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`
        : 'No vehicle',
      name: product.name,
      acquisition_cost: product.acquisition_cost,
      price: product.price,
      quantity: 1, // Default quantity
      category_name: product.category_name
    };

    setSelectedProducts(prev => [...prev, newProduct]);
    
    // Load product photo if available
    try {
      const photoMap = await ProductPhotoService.getPhotosByStockItemIds([product.id]);
      if (photoMap && photoMap[product.id] && photoMap[product.id].photo_url) {
        setProductPhotos(prev => ({
          ...prev,
          [product.id]: photoMap[product.id].photo_url
        }));
      }
    } catch (error) {
      console.error('Error loading product photo:', error);
    }
    
    setIsSelectInventoryItemModalOpen(false);
  };

  const handleRemoveService = (index: number) => {
    setSelectedServices(prev => prev.filter((_, i) => i !== index));
  };

  const handleRemoveProduct = (index: number) => {
    setSelectedProducts(prev => prev.filter((_, i) => i !== index));
  };

  const handlePriceChange = (index: number, newPrice: number) => {
    setSelectedServices(prev => prev.map((service, i) => {
      if (i === index) {
        // Recalculate discount amount if discount exists
        let discountAmount = 0;
        if (service.discountValue && service.discountValue > 0) {
          if (service.discountType === 'percentage') {
            discountAmount = newPrice * (service.discountValue / 100);
          } else {
            discountAmount = service.discountValue;
          }
        }
        
        // Calculate discounted price for display
        const discountedPrice = Math.max(0, newPrice - discountAmount);
        
        return { 
          ...service, 
          price: newPrice, // This is the original price
          discountAmount,
          discountedPrice
        };
      }
      return service;
    }));
  };

  const handleServiceNameChange = (index: number, newName: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, name: newName } : service
    ));
  };

  const handleServiceDescriptionChange = (index: number, newDescription: string) => {
    setSelectedServices(prev => prev.map((service, i) => 
      i === index ? { ...service, description: newDescription } : service
    ));
  };

  const handleServiceDiscountChange = (index: number, discountType: 'percentage' | 'dollar', discountValue: number) => {
    setSelectedServices(prev => prev.map((service, i) => {
      if (i === index) {
        // Calculate discount amount for display purposes
        let discountAmount = 0;
        if (discountValue > 0) {
          if (discountType === 'percentage') {
            discountAmount = service.price * (discountValue / 100);
          } else {
            discountAmount = discountValue;
          }
        }
        
        // Calculate discounted price for display (price remains original price in database)
        const discountedPrice = Math.max(0, service.price - discountAmount);
        
        return {
          ...service,
          discountType,
          discountValue,
          discountAmount,
          discountedPrice // For display only
        };
      }
      return service;
    }));
  };



  // Helper function to generate offer title based on index
  const getOfferTitle = (index: number): string => {
    const titles = [
      'Primary offer',
      'Secondary offer',
      'Third offer',
      'Fourth offer',
      'Fifth offer',
      'Sixth offer',
      'Seventh offer',
      'Eighth offer',
      'Ninth offer',
      'Tenth offer'
    ];
    
    if (index < titles.length) {
      return titles[index];
    }
    
    // For offers beyond the 10th, use ordinal numbers
    const ordinals = ['th', 'st', 'nd', 'rd'];
    const v = index + 1;
    const suffix = ordinals[(v % 100 - 20) % 10] || ordinals[v % 10] || ordinals[0];
    return `${v}${suffix} offer`;
  };

  const handleAddOffer = () => {
    setIsOfferModalOpen(true);
    // Auto-populate title based on number of existing offers
    const nextOfferIndex = offers.length;
    setOfferTitle(getOfferTitle(nextOfferIndex));
    setShowCustomerAddress(false);
    setSelectedServices([]);
    setCurrentVehicle(null);
    setRequireDeposit(false);
    setSaveCardOnDeposit(false);
    setOfferDepositPercentage('');
    setSelectedDepositPercentage(null);
  };

  // ── Per-service photos (builder) ───────────────────────────────
  const servicePhotoTotal = (s: ServiceOption) => (s.servicePhotos?.length || 0) + (s.pendingPhotos?.length || 0);

  const addServicePendingPhotos = (offerIndex: number, serviceIndex: number, files: File[]) => {
    setOffers(prev => prev.map((o, oi) => oi !== offerIndex ? o : {
      ...o,
      services: o.services.map((s, si) => {
        if (si !== serviceIndex) return s;
        const remaining = MAX_PHOTOS_PER_SERVICE - servicePhotoTotal(s);
        const toAdd = files.slice(0, Math.max(0, remaining));
        return { ...s, pendingPhotos: [...(s.pendingPhotos || []), ...toAdd] };
      }),
    }));
  };

  const removeServicePendingPhoto = (offerIndex: number, serviceIndex: number, idx: number) => {
    setOffers(prev => prev.map((o, oi) => oi !== offerIndex ? o : {
      ...o,
      services: o.services.map((s, si) => si !== serviceIndex ? s : {
        ...s, pendingPhotos: (s.pendingPhotos || []).filter((_, i) => i !== idx),
      }),
    }));
  };

  const removeServiceExistingPhoto = (offerIndex: number, serviceIndex: number, photoId: string) => {
    setOffers(prev => prev.map((o, oi) => oi !== offerIndex ? o : {
      ...o,
      services: o.services.map((s, si) => si !== serviceIndex ? s : {
        ...s, servicePhotos: (s.servicePhotos || []).filter(p => p.id !== photoId),
      }),
    }));
  };

  const bustedPhotoUrl = (filePath: string, photoId: string) => {
    const url = proposalServicePhotoUrl(filePath);
    return photoBust[photoId] ? `${url}${url.includes('?') ? '&' : '?'}t=${photoBust[photoId]}` : url;
  };

  const openServicePhotoViewer = (offerIndex: number, realIndex: number, service: ServiceOption, startIndex: number) => {
    const existing = service.servicePhotos || [];
    const pending = service.pendingPhotos || [];
    const images = [
      ...existing.map((p) => ({ src: bustedPhotoUrl(p.file_path, p.id), caption: service.name })),
      ...pending.map((f) => ({ src: URL.createObjectURL(f), caption: service.name })),
    ];
    openImageViewer({
      images,
      index: startIndex,
      onSaveEdit: async (idx, blob) => {
        if (idx < existing.length) {
          // Already-saved photo: overwrite the storage file in place, then
          // cache-bust the thumbnail so it refetches the edited image.
          const photo = existing[idx];
          try {
            await supabase.storage.from('user-uploads').upload(photo.file_path, blob, { upsert: true, contentType: 'image/jpeg', cacheControl: '0' });
            setPhotoBust((prev) => ({ ...prev, [photo.id]: Date.now() }));
          } catch (e) {
            console.error('Error saving edited photo:', e);
          }
        } else {
          // Not-yet-uploaded photo: swap the local File so the edit is the one
          // that gets uploaded when the proposal is saved.
          const pIdx = idx - existing.length;
          const orig = pending[pIdx];
          const edited = new File([blob], orig?.name || `edited-${Date.now()}.jpg`, { type: 'image/jpeg' });
          setOffers((prev) => prev.map((o, oi) => oi !== offerIndex ? o : {
            ...o,
            services: o.services.map((s, si) => si !== realIndex ? s : {
              ...s,
              pendingPhotos: (s.pendingPhotos || []).map((f, i) => (i === pIdx ? edited : f)),
            }),
          }));
        }
      },
    });
  };

  const urlToFile = async (url: string, name: string): Promise<File> => {
    const res = await fetch(url);
    const blob = await res.blob();
    return new File([blob], name, { type: blob.type || 'image/jpeg' });
  };

  const handleAddPhotoClick = (offerIndex: number, serviceIndex: number) => {
    if (fieldQuotePhotoGroups.length > 0 || bookingPhotos.length > 0) {
      setAddPhotoTarget({ offerIndex, serviceIndex });
    } else {
      addFileTargetRef.current = { offerIndex, serviceIndex };
      addFileInputRef.current?.click();
    }
  };

  const chooseNewPhoto = () => {
    const t = addPhotoTarget;
    setAddPhotoTarget(null);
    if (!t) return;
    addFileTargetRef.current = t;
    addFileInputRef.current?.click();
  };

  const openLinkedPicker = () => {
    const t = addPhotoTarget;
    setAddPhotoTarget(null);
    if (!t) return;
    setLinkedSelected(new Set());
    setLinkedPickerTarget(t);
  };

  const confirmLinkedPhotos = async () => {
    const t = linkedPickerTarget;
    setLinkedPickerTarget(null);
    if (!t) return;
    const service = offers[t.offerIndex]?.services[t.serviceIndex];
    const remaining = service ? MAX_PHOTOS_PER_SERVICE - servicePhotoTotal(service) : 0;
    const allPhotos = fieldQuotePhotoGroups.flatMap((g) => g.photos);
    const chosen = allPhotos.filter((p) => linkedSelected.has(p.id)).slice(0, Math.max(0, remaining));
    if (!chosen.length) return;
    try {
      const files = await Promise.all(
        chosen.map((p, i) => urlToFile(fieldQuotePhotoUrl(p.file_path), p.file_name || `field-quote-${Date.now()}-${i}.jpg`)),
      );
      addServicePendingPhotos(t.offerIndex, t.serviceIndex, files);
    } catch (e) {
      console.error('Error importing field quote photos:', e);
    }
  };

  const openBookingPicker = () => {
    const t = addPhotoTarget;
    setAddPhotoTarget(null);
    if (!t) return;
    setBookingSelected(new Set());
    setBookingPickerTarget(t);
  };

  const confirmBookingPhotos = async () => {
    const t = bookingPickerTarget;
    setBookingPickerTarget(null);
    if (!t) return;
    const service = offers[t.offerIndex]?.services[t.serviceIndex];
    const remaining = service ? MAX_PHOTOS_PER_SERVICE - servicePhotoTotal(service) : 0;
    const chosen = bookingPhotos.filter((p) => bookingSelected.has(p.id)).slice(0, Math.max(0, remaining));
    if (!chosen.length) return;
    try {
      const files = await Promise.all(
        chosen.map((p, i) => urlToFile(p.url, p.file_name || `booking-${Date.now()}-${i}.jpg`)),
      );
      addServicePendingPhotos(t.offerIndex, t.serviceIndex, files);
    } catch (e) {
      console.error('Error importing booking photos:', e);
    }
  };

  const onAddFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const t = addFileTargetRef.current;
    addFileTargetRef.current = null;
    e.currentTarget.value = '';
    if (t && files.length) addServicePendingPhotos(t.offerIndex, t.serviceIndex, files);
  };

  const renderServicePhotoStrip = (offerIndex: number, service: ServiceOption) => {
    const realIndex = offers[offerIndex]?.services.indexOf(service);
    if (realIndex == null || realIndex < 0) return null;
    const existing = service.servicePhotos || [];
    const pending = service.pendingPhotos || [];
    const total = existing.length + pending.length;
    return (
      <div className="mt-1.5 ml-4 pl-2 border-l-2 border-gray-100">
        <div className="flex items-center flex-wrap gap-2">
          {existing.map((p, ei) => (
            <div key={p.id} className="relative h-12 w-12 shrink-0">
              <img src={bustedPhotoUrl(p.file_path, p.id)} alt="" onClick={() => openServicePhotoViewer(offerIndex, realIndex, service, ei)} className="h-full w-full object-cover rounded-lg border border-gray-200 bg-gray-50 cursor-pointer" />
              <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeServiceExistingPhoto(offerIndex, realIndex, p.id); }}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-gray-900/90 text-white flex items-center justify-center shadow-md z-20" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {pending.map((f, i) => (
            <div key={`p-${i}`} className="relative h-12 w-12 shrink-0">
              <img src={URL.createObjectURL(f)} alt="" onClick={() => openServicePhotoViewer(offerIndex, realIndex, service, existing.length + i)} className="h-full w-full object-cover rounded-lg border border-blue-200 bg-blue-50 cursor-pointer" />
              <button type="button" onMouseDown={(e) => e.stopPropagation()} onClick={(e) => { e.preventDefault(); e.stopPropagation(); removeServicePendingPhoto(offerIndex, realIndex, i); }}
                className="absolute -top-2 -right-2 h-6 w-6 rounded-full bg-gray-900/90 text-white flex items-center justify-center shadow-md z-20" style={{ WebkitTapHighlightColor: 'transparent' }}>
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
          {total < MAX_PHOTOS_PER_SERVICE && (
            <button
              type="button"
              onClick={() => handleAddPhotoClick(offerIndex, realIndex)}
              className="h-12 w-12 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center text-gray-400 hover:border-blue-400 hover:text-blue-500 cursor-pointer transition-colors"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <Plus className="h-5 w-5" />
            </button>
          )}
        </div>
        <p className="text-[10px] text-gray-400 mt-1">Photos for "{service.name}" · {total}/{MAX_PHOTOS_PER_SERVICE}</p>
      </div>
    );
  };

  const handleEditOffer = (offer: Offer) => {
    setCurrentOffer(offer);
    setOfferTitle(offer.title);
    setShowCustomerAddress(!!offer.show_customer_address);
    setRequireDeposit(offer.deposit_percentage ? true : false);
    setSaveCardOnDeposit(!!offer.save_card_on_deposit);
    setOfferDepositPercentage(offer.deposit_percentage ? offer.deposit_percentage.toString() : '');
    setSelectedDepositPercentage(null); // Reset percentage when editing
    setSelectedServices(offer.services);
    setSelectedProducts(offer.products || []);
    
    // Load product photos for editing
    if (offer.products && offer.products.length > 0) {
      const loadProductPhotos = async () => {
        try {
          const productIds = offer.products!.map(p => p.id);
          const photoMap = await ProductPhotoService.getPhotosByStockItemIds(productIds);
          const photoUrls: Record<string, string> = {};
          if (photoMap && typeof photoMap === 'object') {
            Object.entries(photoMap).forEach(([itemId, photo]) => {
              if (photo && photo.photo_url) {
                photoUrls[itemId] = photo.photo_url;
              }
            });
          }
          setProductPhotos(prev => ({ ...prev, ...photoUrls }));
        } catch (error) {
          console.error('Error loading product photos for editing:', error);
        }
      };
      loadProductPhotos();
    }
    
    if (offer.services.length > 0) {
      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === offer.services[0].vehicleId);
      setCurrentVehicle(vehicle || null);
    } else if (offer.products && offer.products.length > 0) {
      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === offer.products![0].vehicleId);
      setCurrentVehicle(vehicle || null);
    }
    setIsOfferModalOpen(true);
  };

  const handleSaveOffer = () => {
    if (offerTitle && (selectedServices.length > 0 || selectedProducts.length > 0)) {
      // Only set deposit percentage if requireDeposit is checked
      const depositPercentage = requireDeposit && offerDepositPercentage && offerDepositPercentage.trim() !== '' 
        ? parseFloat(offerDepositPercentage) 
        : null;
      
      const stripeOk =
        !!selectedBusiness?.stripe_connect_account_id && !!selectedBusiness?.stripe_connect_charges_enabled;
      const offerData = {
        title: offerTitle,
        services: selectedServices,
        products: selectedProducts,
        deposit_percentage: depositPercentage && !isNaN(depositPercentage) && depositPercentage >= 0 && depositPercentage <= 100 ? depositPercentage : null,
        save_card_on_deposit:
          !!(requireDeposit && depositPercentage && saveCardOnDeposit && stripeOk && selectedCustomer?.id),
        show_customer_address: !!showCustomerAddress,
      };

      if (currentOffer) {
        setOffers(prev => prev.map(offer => 
          offer === currentOffer ? offerData : offer
        ));
      } else {
        setOffers(prev => [...prev, offerData]);
      }
      setIsOfferModalOpen(false);
      setOfferTitle('');
      setShowCustomerAddress(false);
      setRequireDeposit(false);
      setSaveCardOnDeposit(false);
      setOfferDepositPercentage('');
      setSelectedDepositPercentage(null);
      setSelectedServices([]);
      setSelectedProducts([]);
      setProductPhotos({});
      setCurrentVehicle(null);
      setCurrentOffer(null);
    }
  };

  const handleRemoveOffer = (index: number) => {
    setOffers(prev => prev.filter((_, i) => i !== index));
  };

  const handleTransitionEnd = () => {
    if (!isOpen) {
      setIsAnimating(false);
    }
  };

  // Restore scroll position after deposit checkbox toggles (avoid modal jump)
  useEffect(() => {
    if (!isOfferModalOpen || !offerModalScrollRef.current || !didToggleDepositRef.current) return;
    didToggleDepositRef.current = false;
    const el = offerModalScrollRef.current;
    const saved = savedOfferScrollTopRef.current;
    const raf = requestAnimationFrame(() => {
      el.scrollTop = saved;
    });
    return () => cancelAnimationFrame(raf);
  }, [requireDeposit, isOfferModalOpen]);

  // Update customer validation when customer changes
  useEffect(() => {
    setIsCustomerValid(!!selectedCustomer);
  }, [selectedCustomer]);

  const vehiclesRequired = selectedBusiness?.industry !== 'Home Services' && !templateMode && !!selectedCustomer;

  const handleAssignVehicleToOffer = (vehicle: CustomerVehicle, offerIndex: number) => {
    const vehicleName = `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
    setOffers(prev => prev.map((offer, i) => {
      if (i !== offerIndex) return offer;
      return {
        ...offer,
        services: offer.services.map(s => ({
          ...s,
          vehicleId: s.vehicleId === 'no-vehicle' ? vehicle.id : s.vehicleId,
          vehicleName: s.vehicleId === 'no-vehicle' ? vehicleName : s.vehicleName,
        })),
        products: (offer.products || []).map(p => ({
          ...p,
          vehicleId: p.vehicleId === 'no-vehicle' ? vehicle.id : p.vehicleId,
          vehicleName: p.vehicleId === 'no-vehicle' ? vehicleName : p.vehicleName,
        })),
      };
    }));
    setIsAssignVehicleModalOpen(false);
    setAssignVehicleOfferIndex(null);
  };
  const offersNeedVehicles = vehiclesRequired && offers.length > 0 && offers.some(offer =>
    offer.services.some(s => s.vehicleId === 'no-vehicle') ||
    (offer.products || []).some(p => p.vehicleId === 'no-vehicle')
  );

  // Mirrors the "Create and review" button's validation exactly, so the AI
  // knows when the form is genuinely ready (appended to every handler result).
  // Overrides exist because React state hasn't committed inside a handler.
  const proposalReadinessLine = (overrides?: { title?: string; offers?: Offer[] }) => {
    const effectiveTitle = (overrides?.title ?? title).trim();
    const effectiveOffers = overrides?.offers ?? offersLiveRef.current;
    const needVehicles = vehiclesRequired && effectiveOffers.length > 0 && effectiveOffers.some(offer =>
      offer.services.some(s => s.vehicleId === 'no-vehicle') ||
      (offer.products || []).some(p => p.vehicleId === 'no-vehicle')
    );
    const missing: string[] = [];
    if (!isCustomerValid) missing.push('a customer');
    if (!effectiveTitle) missing.push('a title');
    if (effectiveOffers.length === 0) missing.push('at least one offer — STOP: did the user actually NAME what goes in it? A proposal title ("spring special") is NOT offer content. If they never said what to include, do NOT call get_services or add_proposal_offer — ask what the offer should include. Only when they named the contents: get_services, then add_proposal_offer');
    else if (needVehicles) missing.push('a vehicle assigned to every offer item (the user assigns them in the form)');
    return missing.length === 0
      ? ' FORM STATUS: READY — every required field is filled and the "Create and review" button is active. READY does NOT mean done: if the user asked for anything not built yet (another offer, an edit, a rename), do that FIRST before saying anything. Only when EVERYTHING the user named is done, tell them the proposal is ready and ask if you should create it. Press "Create and review" ONLY after they confirm; it creates a DRAFT they can review and then send.'
      : ` FORM STATUS: not ready to create yet — still needs ${missing.join(', ')}. For any missing piece the user hasn't named, ASK or guide them — never pick a value yourself.`;
  };

  /**
   * Resolve which vehicle AI-built offer lines should use (automotive only).
   * Deliberately hard to get stuck on: no query → main/first vehicle; "any" →
   * first; a named query → first match. Vehicles are described by MODEL only —
   * database ids are never surfaced.
   */
  const resolveOfferVehicle = (
    vehicleQuery: string,
    vehicleId: string,
  ): { vehicle: CustomerVehicle | null } | { error: string } => {
    if (!vehiclesRequired) return { vehicle: null };
    const vehicles = selectedCustomer?.vehicles || [];
    if (vehicles.length === 0) return { vehicle: null }; // committed as no-vehicle; FORM STATUS reports it
    // Model-only names for the user; a year prefix only when models repeat.
    const modelNames = vehicles.map((v) =>
      vehicles.some((o) => o !== v && o.model === v.model) ? `${v.year} ${v.model}` : v.model,
    );
    if (vehicleId) {
      const byId = vehicles.find((v) => v.id === vehicleId);
      if (byId) return { vehicle: byId };
    }
    if (vehicleQuery) {
      if (/^(any|first|whatever|any\s*one|doesn'?t\s*matter|no\s*preference)$/i.test(vehicleQuery)) {
        return { vehicle: vehicles[0] };
      }
      const tokens = vehicleQuery.toLowerCase().split(/\s+/).filter(Boolean);
      const matches = vehicles.filter((v) =>
        tokens.every((t) => `${v.year} ${v.make} ${v.model} ${v.color || ''} ${v.license_plate || ''}`.toLowerCase().includes(t)),
      );
      if (matches.length > 0) return { vehicle: matches[0] };
      return {
        error: `error: none of the customer's vehicles match "${vehicleQuery}". Their vehicles: ${modelNames.join(', ')}. Refer to them by model only (never ids); pass vehicle with one of these, or "any" if the user doesn't care.`,
      };
    }
    return { vehicle: vehicles.find((v) => v.is_primary) || vehicles[0] };
  };

  /** Build ServiceOption lines from the executor's resolved payload (mirrors handleAddService). */
  const buildOfferServiceLines = (rawLines: any[], vehicle: CustomerVehicle | null): ServiceOption[] =>
    rawLines.map((line) => {
      const { service, priceOption } = line;
      const pricingType = priceOption.pricing_type || 'flat';
      const isPerUnit = pricingType === 'per_unit' || pricingType === 'per_sqft';
      const qty = isPerUnit ? Number(line.quantity) || 0 : undefined;
      let price = isPerUnit
        ? Math.round((qty || 0) * (priceOption.unit_price || 0) * 100) / 100
        : priceOption.price;
      let duration = isPerUnit
        ? Math.round((qty || 0) * (priceOption.duration_per_unit || 0) * 60)
        : priceOption.duration_minutes;
      if (line.priceOverride !== null && line.priceOverride !== undefined) price = line.priceOverride;
      const durationOverridden = line.durationMinutesOverride !== null && line.durationMinutesOverride !== undefined;
      if (durationOverridden) duration = line.durationMinutesOverride;
      return {
        id: service.id,
        vehicleId: vehicle?.id || 'no-vehicle',
        vehicleName: vehicle ? `${vehicle.year} ${vehicle.make} ${vehicle.model}` : 'No vehicle',
        name: line.titleOverride || service.name,
        originalName: service.name,
        description: line.descriptionOverride || service.description || '',
        originalDescription: service.description || '',
        priceOptionId: priceOption.id,
        price,
        duration_minutes: duration,
        isSelectable: false,
        pricing_type: pricingType,
        unit_price: isPerUnit ? (priceOption.unit_price || 0) : undefined,
        duration_per_unit: isPerUnit ? (priceOption.duration_per_unit || 0) : undefined,
        quantity: qty,
        compare_at_price: priceOption.compare_at_price ?? null,
        ...(durationOverridden ? { _durationOverridden: true } : {}),
      };
    });

  const describeOfferList = (list: Offer[]) =>
    list.map((o, i) => `${i + 1}. "${o.title}" — ${o.services.map((s) => s.name).join(' + ') || 'no services'}`).join('; ');

  /** Match an offer by title words, service names, or 1-based position (reads the live ref). */
  const findOfferIndex = (query: string): { index: number } | { error: string } => {
    const offersNow = offersLiveRef.current;
    if (offersNow.length === 0) return { error: 'error: this proposal has no offers yet — add one with add_proposal_offer first.' };
    const q = query.trim().toLowerCase();
    if (!q) {
      return offersNow.length === 1
        ? { index: 0 }
        : { error: `error: there are ${offersNow.length} offers — say which one: ${describeOfferList(offersNow)}. Ask the user if unclear.` };
    }
    const ordinals: Record<string, number> = { first: 0, second: 1, third: 2, fourth: 3, fifth: 4, last: offersNow.length - 1 };
    if (q in ordinals && ordinals[q] < offersNow.length) return { index: ordinals[q] };
    if (/^\d+$/.test(q)) {
      const idx = parseInt(q, 10) - 1;
      return idx >= 0 && idx < offersNow.length
        ? { index: idx }
        : { error: `error: there is no offer ${q} — the proposal has ${offersNow.length}: ${describeOfferList(offersNow)}.` };
    }
    const tokens = q.replace(/\boffer\b/g, ' ').split(/\s+/).filter(Boolean);
    const titleMatches = offersNow
      .map((o, i) => ({ o, i }))
      .filter(({ o }) => tokens.length > 0 && tokens.every((t) => o.title.toLowerCase().includes(t)));
    if (titleMatches.length === 1) return { index: titleMatches[0].i };
    if (titleMatches.length > 1) return { error: `error: ${titleMatches.length} offers match "${query}": ${describeOfferList(titleMatches.map(({ o }) => o))}. Ask the user which one.` };
    // Fall back to the services inside ("the interior offer" when titles are default)
    const serviceMatches = offersNow
      .map((o, i) => ({ o, i }))
      .filter(({ o }) => tokens.length > 0 && o.services.some((s) => tokens.every((t) => s.name.toLowerCase().includes(t))));
    if (serviceMatches.length === 1) return { index: serviceMatches[0].i };
    return { error: `error: ${serviceMatches.length === 0 ? 'no offer matches' : `${serviceMatches.length} offers match`} "${query}". The offers: ${describeOfferList(offersNow)}. Ask the user which one.` };
  };

  const describeOfferResult = (offer: Offer) => {
    const total = offer.services.reduce((sum, s) => sum + (s.price || 0), 0);
    const lines = offer.services.map((s) => `"${s.name}" $${s.price}`).join(', ');
    const deposit = offer.deposit_percentage
      ? `, ${offer.deposit_percentage}% deposit ($${Math.round(total * offer.deposit_percentage) / 100})`
      : '';
    return `"${offer.title}": ${lines} — total $${total}${deposit}`;
  };

  // Assistant surface handlers — reassigned every render so they always read
  // current state (same aiBridgeRef pattern as CreateBookingModal/CreateTaskModal).
  const aiBridgeRef = useRef<{
    setDetails: (detail: any) => string;
    addOffer: (detail: any) => string;
    updateOffer: (detail: any) => string;
    updateOfferService: (detail: any) => string;
  }>({ setDetails: () => '', addOffer: () => '', updateOffer: () => '', updateOfferService: () => '' });
  aiBridgeRef.current = {
    setDetails: (detail) => {
      const changed: string[] = [];
      const newTitle = String(detail?.title ?? '').trim().slice(0, 200);
      if (newTitle) {
        setTitle(newTitle);
        changed.push(`title set to "${newTitle}"`);
      }
      const expiryDays = Number(detail?.expiryDays) || 0;
      const expiryDate = String(detail?.expiryDate ?? '').trim();
      if (expiryDate) {
        if (!/^\d{4}-\d{2}-\d{2}$/.test(expiryDate)) return 'error: expiry_date must be YYYY-MM-DD.';
        setExpiryOption('custom');
        setShowCustomDate(true);
        setCustomExpiryDate(expiryDate);
        changed.push(`expiry set to ${expiryDate}`);
      } else if (expiryDays) {
        if (![15, 30, 60].includes(expiryDays)) {
          return 'error: expiry_days must be 15, 30, or 60 — for any other day pass expiry_date as YYYY-MM-DD.';
        }
        setExpiryOption(String(expiryDays));
        setShowCustomDate(false);
        changed.push(`expiry set to ${expiryDays} days from now`);
      }
      if (changed.length === 0) return 'error: nothing to set — pass a title and/or an expiry.';
      // React state hasn't committed yet — compute readiness with the new title applied.
      return `Proposal ${changed.join(', ')}.${proposalReadinessLine({ title: newTitle || undefined })}`;
    },

    addOffer: (detail) => {
      const rawLines = Array.isArray(detail?.lines) ? detail.lines : [];
      if (rawLines.length === 0) return 'error: the offer needs at least one service line.';
      const vehicleResult = resolveOfferVehicle(String(detail?.vehicleQuery || ''), String(detail?.vehicleId || ''));
      if ('error' in vehicleResult) return vehicleResult.error;

      const depositPercentage = detail?.depositPercentage != null ? Number(detail.depositPercentage) : null;
      const stripeOk = !!selectedBusiness?.stripe_connect_account_id && !!selectedBusiness?.stripe_connect_charges_enabled;
      if (depositPercentage !== null && !stripeOk) {
        return 'error: deposits need Stripe connected (Invoices app → Stripe Integration), which this business hasn\'t set up — add the offer without a deposit or tell the user.';
      }

      const services = buildOfferServiceLines(rawLines, vehicleResult.vehicle);
      const offersNow = offersLiveRef.current;
      const newOffer: Offer = {
        title: String(detail?.title || '').trim() || getOfferTitle(offersNow.length),
        services,
        products: [],
        deposit_percentage: depositPercentage,
        save_card_on_deposit: !!(depositPercentage && detail?.saveCardOnDeposit !== false && stripeOk && selectedCustomer?.id),
        show_customer_address: false,
      };
      const nextOffers = [...offersNow, newOffer];
      commitOffers(nextOffers);
      const vehicleNote = vehicleResult.vehicle ? ` (on the ${vehicleResult.vehicle.model})` : '';
      return `Added offer ${describeOfferResult(newOffer)}${vehicleNote}. The proposal now has ${nextOffers.length} offer${nextOffers.length === 1 ? '' : 's'}.${proposalReadinessLine({ offers: nextOffers })}`;
    },

    updateOffer: (detail) => {
      const found = findOfferIndex(String(detail?.offerQuery || ''));
      if ('error' in found) return found.error;
      const idx = found.index;
      const changed: string[] = [];
      const offersNow = offersLiveRef.current;
      const offer = { ...offersNow[idx], services: [...offersNow[idx].services] };

      const newTitle = String(detail?.title || '').trim();
      if (newTitle) {
        offer.title = newTitle.slice(0, 120);
        changed.push(`renamed to "${offer.title}"`);
      }
      if (detail?.depositPercentage !== undefined) {
        if (detail.depositPercentage === null) {
          offer.deposit_percentage = null;
          offer.save_card_on_deposit = false;
          changed.push('deposit removed');
        } else {
          const stripeOk = !!selectedBusiness?.stripe_connect_account_id && !!selectedBusiness?.stripe_connect_charges_enabled;
          if (!stripeOk) return 'error: deposits need Stripe connected (Invoices app → Stripe Integration), which this business hasn\'t set up.';
          offer.deposit_percentage = Number(detail.depositPercentage);
          if (offer.save_card_on_deposit === undefined || offer.save_card_on_deposit === null) {
            offer.save_card_on_deposit = !!selectedCustomer?.id;
          }
          changed.push(`deposit set to ${offer.deposit_percentage}%`);
        }
      }
      if (typeof detail?.saveCardOnDeposit === 'boolean') {
        offer.save_card_on_deposit = detail.saveCardOnDeposit && !!offer.deposit_percentage && !!selectedCustomer?.id;
        changed.push(`card saving ${detail.saveCardOnDeposit ? 'on' : 'off'}`);
      }
      const removeService = String(detail?.removeService || '').trim().toLowerCase();
      if (removeService) {
        const tokens = removeService.split(/\s+/).filter(Boolean);
        const matches = offer.services.filter((s) => tokens.every((t) => s.name.toLowerCase().includes(t)));
        if (matches.length !== 1) {
          return `error: ${matches.length === 0 ? 'no service matches' : `${matches.length} services match`} "${detail.removeService}" in "${offer.title}". Its services: ${offer.services.map((s) => `"${s.name}"`).join(', ')}. Ask the user which one.`;
        }
        offer.services = offer.services.filter((s) => s !== matches[0]);
        changed.push(`removed "${matches[0].name}"`);
        if (offer.services.length === 0 && (offer.products || []).length === 0) {
          return `error: removing "${matches[0].name}" would leave "${offer.title}" empty — every offer needs at least one service. Remove the whole offer instead? Ask the user.`;
        }
      }
      if (Array.isArray(detail?.addLines) && detail.addLines.length > 0) {
        // New lines follow the offer's existing vehicle (or none)
        const existingVehicleId = offer.services.find((s) => s.vehicleId !== 'no-vehicle')?.vehicleId;
        const vehicle = (selectedCustomer?.vehicles || []).find((v) => v.id === existingVehicleId) || null;
        const added = buildOfferServiceLines(detail.addLines, vehicle);
        offer.services = [...offer.services, ...added];
        changed.push(`added ${added.map((s) => `"${s.name}"`).join(', ')}`);
      }
      if (changed.length === 0) return 'error: nothing to change — pass title, deposit_percentage, save_card_on_deposit, add_services, or remove_service.';

      const nextOffers = offersNow.map((o, i) => (i === idx ? offer : o));
      commitOffers(nextOffers);
      return `Offer ${changed.join(', ')} — now ${describeOfferResult(offer)}.${proposalReadinessLine({ offers: nextOffers })}`;
    },

    updateOfferService: (detail) => {
      const found = findOfferIndex(String(detail?.offerQuery || ''));
      if ('error' in found) return found.error;
      const idx = found.index;
      const offersNow = offersLiveRef.current;
      const offer = { ...offersNow[idx], services: [...offersNow[idx].services] };

      const serviceQuery = String(detail?.serviceQuery || '').trim().toLowerCase();
      let lineIdx = -1;
      if (!serviceQuery) {
        if (offer.services.length !== 1) {
          return `error: "${offer.title}" has ${offer.services.length} services — say which one: ${offer.services.map((s) => `"${s.name}"`).join(', ')}.`;
        }
        lineIdx = 0;
      } else {
        const tokens = serviceQuery.split(/\s+/).filter(Boolean);
        const matches = offer.services.map((s, i) => ({ s, i })).filter(({ s }) => tokens.every((t) => s.name.toLowerCase().includes(t)));
        if (matches.length !== 1) {
          return `error: ${matches.length === 0 ? 'no service matches' : `${matches.length} services match`} "${detail.serviceQuery}" in "${offer.title}". Its services: ${offer.services.map((s) => `"${s.name}"`).join(', ')}. Ask the user which one.`;
        }
        lineIdx = matches[0].i;
      }

      const line = { ...offer.services[lineIdx] };
      const changed: string[] = [];
      if (detail?.newTitle) {
        line.name = String(detail.newTitle).slice(0, 200);
        changed.push(`renamed to "${line.name}"`);
      }
      if (detail?.newDescription) {
        line.description = String(detail.newDescription).slice(0, 2000);
        changed.push('description updated');
      }
      if (detail?.newPrice !== null && detail?.newPrice !== undefined) {
        line.price = Number(detail.newPrice);
        changed.push(`price set to $${line.price}`);
      }
      if (detail?.newDurationMinutes !== null && detail?.newDurationMinutes !== undefined) {
        line.duration_minutes = Number(detail.newDurationMinutes);
        line._durationOverridden = true;
        changed.push(`duration set to ${Math.round((line.duration_minutes / 60) * 100) / 100}h`);
      }
      if (changed.length === 0) return 'error: nothing to change — pass new_price, new_title, new_description, or new_duration_hours.';

      offer.services = offer.services.map((s, i) => (i === lineIdx ? line : s));
      const nextOffers = offersNow.map((o, i) => (i === idx ? offer : o));
      commitOffers(nextOffers);
      return `Updated "${line.name}" in "${offer.title}" — ${changed.join(', ')} (this proposal only, the catalog is unchanged). Now ${describeOfferResult(offer)}.${proposalReadinessLine({ offers: nextOffers })}`;
    },
  };

  useEffect(() => {
    if (!isOpen || templateMode) return;
    return registerAssistantSurface('proposal', {
      setDetails: (payload) => aiBridgeRef.current.setDetails(payload),
      addOffer: (payload) => aiBridgeRef.current.addOffer(payload),
      updateOffer: (payload) => aiBridgeRef.current.updateOffer(payload),
      updateOfferService: (payload) => aiBridgeRef.current.updateOfferService(payload),
      cancel: () => {
        handleClose();
        return 'Cancelled — the New Proposal form is closed and nothing was saved.';
      },
    });
  }, [isOpen, templateMode]);

  const handleVehicleCreated = async (vehicle: any) => {
    // Update customer data to get the new vehicle
    if (selectedCustomer?.id) {
      try {
        const updatedCustomer = await getCustomer(selectedCustomer.id);
        setSelectedCustomer(updatedCustomer);
      } catch (error) {
        console.error('Failed to refresh customer data:', error);
        // Fallback: manually add the vehicle to the current customer
        if (selectedCustomer && vehicle) {
          const updatedCustomer = {
            ...selectedCustomer,
            vehicles: [...(selectedCustomer.vehicles || []), vehicle]
          };
          setSelectedCustomer(updatedCustomer);
        }
      }
    }
    
    // Set the new vehicle as current and open service selection
    setCurrentVehicle(vehicle);
    setIsCreateVehicleModalOpen(false);
    setIsSelectServiceModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setShowValidationMessages(true);

    if (templateMode) {
      await handleSaveTemplate();
      return;
    }

    if (!selectedBusiness?.id || !isCustomerValid) {
      setError('Please select a customer');
      return;
    }

    if (offers.length === 0) {
      setError('Please add at least one offer');
      return;
    }

    // Check for customer conflict if project is selected
    if (selectedProjectId && selectedProjectCustomerId && selectedProjectCustomerId !== selectedCustomer?.id) {
      try {
        const { data: existingCustomer, error: customerError } = await supabase
          .from('customers')
          .select('first_name, last_name')
          .eq('id', selectedProjectCustomerId)
          .single();

        if (!customerError && existingCustomer) {
          setConflictProjectCustomerName(`${existingCustomer.first_name} ${existingCustomer.last_name}`);
          setShowCustomerConflictModal(true);
          return;
        }
      } catch (error) {
        console.error('Error fetching existing customer:', error);
      }
    }

    await createProposalWithProject();
  };

  const handleSaveTemplate = async () => {
    if (!selectedBusiness?.id) return;

    if (!templateName.trim()) {
      setError('Please enter a template name');
      return;
    }
    if (offers.length === 0) {
      setError('Please add at least one offer');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const templateData: ProposalTemplateData = {
        title: title || undefined,
        expiryOption,
        offers: offers.map(offer => ({
          title: offer.title,
          deposit_percentage: offer.deposit_percentage || null,
          save_card_on_deposit: offer.save_card_on_deposit || null,
          show_customer_address: offer.show_customer_address || null,
          services: offer.services.map(s => ({
            service_id: s.id,
            vehicle_id: s.vehicleId === 'no-vehicle' ? null : s.vehicleId,
            name: s.name,
            description: s.description || '',
            price: s.price,
            discountType: s.discountType,
            discountValue: s.discountValue,
            duration_minutes: s.duration_minutes,
            isSelectable: s.isSelectable || false,
            pricing_type: s.pricing_type || 'flat',
            unit_price: s.unit_price || 0,
            duration_per_unit: s.duration_per_unit || 0,
            quantity: s.quantity || 0,
            compare_at_price: s.compare_at_price ?? null,
          })),
          products: (offer.products || []).map(p => ({
            stock_item_id: p.id,
            vehicle_id: p.vehicleId === 'no-vehicle' ? null : p.vehicleId,
            name: p.name,
            price: p.price,
            quantity: p.quantity,
            category_name: p.category_name,
            discountType: p.discountType,
            discountValue: p.discountValue,
          })),
        })),
      };

      let saved: ProposalTemplate;
      if (editTemplate?.id) {
        saved = await updateProposalTemplate(editTemplate.id, {
          name: templateName.trim(),
          template_data: templateData,
        });
      } else {
        saved = await createProposalTemplate(selectedBusiness.id, templateName.trim(), templateData);
      }

      onTemplateSaved?.(saved);
      onClose();
    } catch (err) {
      console.error('Error saving template:', err);
      setError(err instanceof Error ? err.message : 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const createProposalWithProject = async () => {
    // Calculate expiry date based on selection
    let expiryDate = new Date();
    if (showCustomDate && customExpiryDate) {
      expiryDate = new Date(customExpiryDate);
    } else {
      expiryDate.setDate(expiryDate.getDate() + parseInt(expiryOption));
    }

    try {
      setLoading(true);
      setError(null);

      // Ensure every service line has a stable line_key (so per-service photos
      // survive the delete/recreate of service rows), and persist it to state so
      // the post-save photo upload uses the same keys.
      const offersWithKeys = offers.map(offer => ({
        ...offer,
        services: offer.services.map(s => ({
          ...s,
          line_key: s.line_key || (typeof crypto !== 'undefined' && (crypto as any).randomUUID
            ? crypto.randomUUID()
            : `lk_${Date.now()}_${Math.random().toString(36).slice(2)}`),
        })),
      }));
      setOffers(offersWithKeys);

      const proposalData = {
        business_id: selectedBusiness!.id,
        customer_id: selectedCustomer!.id,
        title,
        valid_until: expiryDate.toISOString(),
        project_id: selectedProjectId || undefined,
        booking_id: initialBookingId || undefined,
        pipeline_opportunities_id: selectedOpportunityId || undefined,
        offers: offersWithKeys.map(offer => ({
          title: offer.title,
          deposit_percentage: offer.deposit_percentage || null,
          save_card_on_deposit: offer.save_card_on_deposit || null,
          show_customer_address: offer.show_customer_address || null,
          services: offer.services.map(service => ({
            service_id: service.id,
            line_key: service.line_key,
            vehicle_id: service.vehicleId === 'no-vehicle' ? null : service.vehicleId,
            name: service.name,
            description: service.description || '',
            price: service.price,
            discountType: service.discountType,
            discountValue: service.discountValue,
            tax_exempt: false,
            tax_rate: 5,
            duration_minutes: service.duration_minutes,
            isSelectable: service.isSelectable || false,
            pricing_type: service.pricing_type || 'flat',
            unit_price: service.unit_price || 0,
            duration_per_unit: service.duration_per_unit || 0,
            quantity: service.quantity || 0,
            compare_at_price: service.compare_at_price ?? null
          })),
          products: (offer.products || []).map(product => ({
            stock_item_id: product.id,
            vehicle_id: product.vehicleId === 'no-vehicle' ? null : product.vehicleId,
            name: product.name,
            price: product.price,
            quantity: product.quantity,
            category_name: product.category_name,
            discountType: product.discountType,
            discountValue: product.discountValue
          }))
        }))
      };

      let resultProposal: Proposal;

      // Persist per-service photos: delete any the user removed (edit), then
      // upload pending files for each line, keyed by its stable line_key.
      const persistServicePhotos = async (proposalId: string) => {
        try {
          setUploadingPhotos(true);
          const survivingIds = new Set<string>();
          const uploads: { lineKey: string; file: File }[] = [];
          for (const off of offersWithKeys) {
            for (const svc of off.services) {
              (svc.servicePhotos || []).forEach((p: ProposalServicePhoto) => survivingIds.add(p.id));
              (svc.pendingPhotos || []).forEach((f: File) => {
                if (svc.line_key) uploads.push({ lineKey: svc.line_key, file: f });
              });
            }
          }
          if (initialProposal?.id) {
            const dbPhotos = await listProposalServicePhotos(proposalId);
            for (const p of dbPhotos) {
              if (!survivingIds.has(p.id)) await deleteProposalServicePhoto(p.id);
            }
          }
          for (const u of uploads) {
            await uploadProposalServicePhoto({ businessId: selectedBusiness!.id, proposalId, lineKey: u.lineKey, file: u.file });
          }
        } catch (error) {
          console.error('Error persisting per-service photos:', error);
          toast({
            title: 'Photo upload failed',
            description: error instanceof Error ? error.message : 'Could not save line-item photos.',
            variant: 'destructive',
            duration: 6000,
          });
        } finally {
          setUploadingPhotos(false);
        }
      };

      // If editing, update existing proposal
      if (initialProposal?.id) {
        resultProposal = await updateProposal(initialProposal.id, proposalData);

        if (resultProposal?.id) {
          await persistServicePhotos(resultProposal.id);
        }

        toast({
          title: 'Proposal Updated',
          description: `Proposal "${title}" updated successfully!`,
          duration: 3000,
        });
      } else {
        // Otherwise, create new proposal
        resultProposal = await createProposal(proposalData);

        if (resultProposal?.id) {
          await persistServicePhotos(resultProposal.id);
        }

        // Save measurements if any
        if (measurements.length > 0 && resultProposal?.id) {
          try {
            const { createMeasurement } = await import('../lib/proposalMeasurements');
            for (const m of measurements) {
              await createMeasurement({
                business_id: selectedBusiness!.id,
                proposal_id: resultProposal.id,
                title: m.title,
                area_sqft: m.area_sqft,
                area_sqm: m.area_sqm,
                perimeter_ft: m.perimeter_ft,
                polygon_coords: m.polygon_coords,
                address: m.address,
                map_center_lat: m.map_center_lat,
                map_center_lng: m.map_center_lng,
                map_zoom: m.map_zoom,
              });
            }
          } catch (err) {
            console.error('Error saving measurements:', err);
          }
        }

        // If a project was linked, update the project with the proposal_id and customer_id
        if (selectedProjectId && resultProposal?.id) {
          try {
            const updateData: { proposal_id: string; customer_id: string } = {
              proposal_id: resultProposal.id,
              customer_id: selectedCustomer!.id
            };

            const { error: updateError } = await supabase
              .from('projects')
              .update(updateData)
              .eq('id', selectedProjectId)
              .eq('business_id', selectedBusiness!.id);

            if (updateError) {
              console.error('Error updating project:', updateError);
              // Don't fail the whole operation, just log the error
            }
          } catch (err) {
            console.error('Error updating project:', err);
            // Don't fail the whole operation
          }
        }

        toast({
          title: 'Proposal Created',
          description: `Proposal "${title}" created successfully!`,
          duration: 3000, // 3 seconds
        });
      }

      // Linking a proposal to a CRM opportunity is an action — record it so the
      // opportunity is marked read.
      if (selectedOpportunityId && resultProposal?.id && selectedCustomer?.id) {
        recordOpportunityActivity({
          businessId: selectedBusiness!.id,
          customerId: selectedCustomer.id,
          activityType: 'proposal_linked',
          activityData: { proposal_id: resultProposal.id },
          specificOpportunityId: selectedOpportunityId,
        }).catch(err => console.error('Error recording proposal_linked activity:', err));
      }

      // Pass the created/updated proposal to the onSuccess callback
      onSuccess?.(resultProposal);
      onClose();
      setShowCustomerConflictModal(false);
    } catch (err) {
      console.error('Error creating proposal:', err);
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen && !isAnimating) return null;

  const isMobile = window.innerWidth < 640;

  // Structure matches CreateBookingModal: no padding on overlay; panel positioned with safe-area top/bottom
  const modalContent = (
    <div 
      className={`fixed inset-0 transition-opacity duration-200 overflow-x-hidden ${
        isOpen ? 'bg-black/50' : 'bg-black/0'
      }`}
      style={{
        zIndex: baseZ,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
      onClick={handleClose}
    >
      <div 
        className={`bg-white transform transition-all duration-200 max-w-full ${
          isMobile 
            ? `fixed left-0 right-0 w-full ${isOpen ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}`
            : `fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl ${
                isOpen ? 'translate-x-0' : 'translate-x-full'
              }`
        }`}
        style={{ 
          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
          top: 'env(safe-area-inset-top, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          ...(isMobile && { left: 0, right: 0 }),
          ...(!isMobile && { height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))' }),
        }}
        onTransitionEnd={handleTransitionEnd}
        onClick={e => e.stopPropagation()}
      >
        <div className="h-full flex flex-col overflow-hidden min-w-0 max-w-full">
          {/* Templates Button (only in normal proposal mode) */}
          {!templateMode && (
            <button
              type="button"
              onClick={() => setIsTemplatesModalOpen(true)}
              className="absolute top-4 left-4 h-8 flex items-center gap-1.5 px-3 text-sm font-medium text-gray-500 hover:text-gray-700 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <FileText className="h-4 w-4" />
              Templates
            </button>
          )}

          {/* Link Chain Button (hidden in template mode) */}
          {!templateMode && (
          <button
            type="button"
            onClick={async () => {
              setIsLinkModalOpen(true);
              if (selectedProjectId) {
                setLinkModalView('projects');
                await fetchProjects();
              } else if (selectedOpportunityId) {
                setLinkModalView('opportunities');
                await fetchOpportunities();
              } else {
                setLinkModalView('options');
              }
            }}
            className={`absolute top-4 right-14 h-8 w-8 flex items-center justify-center rounded-full active:scale-95 transition-all duration-150 touch-manipulation z-10 ${
              (selectedProjectId || selectedOpportunityId)
                ? 'text-blue-600 bg-blue-50 hover:bg-blue-100 hover:text-blue-700' 
                : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100 active:bg-gray-200'
            }`}
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <Link className="h-5 w-5" />
          </button>
          )}

          {/* Close Button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
            style={{ WebkitTapHighlightColor: 'transparent' }}
          >
            <X className="h-5 w-5" />
          </button>

          {/* Header */}
          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
            <h2 className="text-2xl font-bold text-gray-900" style={{ 
              letterSpacing: '-0.02em'
            }}>
              {templateMode
                ? (editTemplate ? 'Edit Template' : 'New Template')
                : 'New Proposal'}
            </h2>
          </div>

          <form 
            onSubmit={handleSubmit} 
            className="flex-1 overflow-y-auto overflow-x-hidden bg-white min-w-0"
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-6 min-w-0 max-w-full`} style={{ paddingBottom: '1rem' }}>
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl text-sm">
                  <div className="flex items-center">
                    <AlertCircle className="h-4 w-4 mr-2 flex-shrink-0" />
                    {error}
                  </div>
                </div>
              )}

              {/* Template Name (only in template mode) */}
              {templateMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Template Name *
                  </label>
                  <input
                    type="text"
                    value={templateName}
                    onChange={(e) => setTemplateName(e.target.value)}
                    placeholder="e.g. Standard Detail Package"
                    className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                    style={{ 
                      fontSize: '16px', 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  />
                </div>
              )}

              {/* Proposal Details Section */}
              <div className="space-y-4">
                {/* Linked Project Field - Conditionally Rendered */}
                {!templateMode && selectedProjectId && selectedProjectTitle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Project
                    </label>
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-700 flex items-center justify-between">
                      <span>{selectedProjectTitle}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedProjectId(null);
                          setSelectedProjectTitle(null);
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                {/* Linked Opportunity Field - Conditionally Rendered */}
                {!templateMode && selectedOpportunityId && selectedOpportunityTitle && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Linked Opportunity
                    </label>
                    <div className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl text-base text-gray-700 flex items-center justify-between">
                      <span>{selectedOpportunityTitle}</span>
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedOpportunityId(null);
                          setSelectedOpportunityTitle(null);
                          setSelectedOpportunityCustomerId(null);
                          userClearedOpportunityLinkRef.current = true;
                        }}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Proposal Title *
                  </label>
                  <input
                    type="text"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Enter proposal title"
                    className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                    style={{ 
                      fontSize: '16px', 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  />
                </div>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date
                    </label>
                    <select
                      value={expiryOption}
                      onChange={(e) => {
                        setExpiryOption(e.target.value);
                        setShowCustomDate(e.target.value === 'custom');
                      }}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                      style={{ 
                        fontSize: '16px', 
                        minHeight: isMobile ? '44px' : '48px',
                        WebkitTapHighlightColor: 'transparent'
                      }}
                    >
                      <option value="60">60 days from now</option>
                      <option value="30">30 days from now</option>
                      <option value="15">15 days from now</option>
                      <option value="custom">Custom date</option>
                    </select>
                  </div>
                  
                  {showCustomDate && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Custom Date
                      </label>
                      <input
                        type="date"
                        value={customExpiryDate}
                        onChange={(e) => setCustomExpiryDate(e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                        className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                        style={{ 
                          fontSize: '16px', 
                          minHeight: isMobile ? '44px' : '48px',
                          WebkitTapHighlightColor: 'transparent'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Google Earth Measurements */}
              {googleEarthEnabled && (
                <div className="space-y-3">
                  {measurements.length > 0 && (
                    <div className="space-y-2">
                      {measurements.map((m, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-xl"
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Ruler className="h-4 w-4 text-green-600" />
                            </div>
                            <div className="min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">{m.title}</div>
                              <div className="text-xs text-gray-500">
                                {m.area_sqft.toLocaleString()} sq ft · {m.perimeter_ft.toLocaleString()} ft perimeter
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-1 flex-shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingMeasurementIndex(idx);
                                setIsMeasurementModalOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            >
                              <Globe className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setMeasurements(prev => prev.filter((_, i) => i !== idx))}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setEditingMeasurementIndex(null);
                      setIsMeasurementModalOpen(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-green-700 hover:text-green-800 bg-green-50 hover:bg-green-100 border border-green-200 rounded-xl transition-colors touch-manipulation"
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <Globe className="h-4 w-4" />
                    + Google Measurement
                  </button>
                </div>
              )}

              {/* Customer Selection Section (hidden in template mode) */}
              {!templateMode && (
              <div className="space-y-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Customer *
                </label>
                
                {selectedCustomer ? (
                  <div 
                    className="flex items-center p-4 bg-white border border-gray-300 rounded-xl cursor-pointer hover:bg-gray-50 active:bg-gray-100 transition-colors touch-manipulation"
                    onClick={() => setIsEditCustomerModalOpen(true)}
                    style={{ WebkitTapHighlightColor: 'transparent' }}
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="h-12 w-12 rounded-full bg-gradient-to-b from-gray-300 to-gray-400 shadow-sm flex items-center justify-center text-white font-medium text-sm flex-shrink-0">
                        {getPersonInitials(selectedCustomer.first_name, selectedCustomer.last_name)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-base font-medium text-gray-900">
                          {selectedCustomer.first_name} {selectedCustomer.last_name}
                        </div>
                        <div className="flex flex-col space-y-0.5 text-sm text-gray-600 mt-1">
                          {selectedCustomer.email && (
                            <div className="flex items-center space-x-1.5">
                              <Mail className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{selectedCustomer.email}</span>
                            </div>
                          )}
                          {selectedCustomer.phone && (
                            <div className="flex items-center space-x-1.5">
                              <Phone className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                              <span>{selectedCustomer.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => setIsSelectCustomerModalOpen(true)}
                    className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all touch-manipulation`}
                    style={{ 
                      minHeight: isMobile ? '44px' : '48px',
                      WebkitTapHighlightColor: 'transparent'
                    }}
                  >
                    Select Customer
                  </button>
                )}
              </div>
              )}

              {/* Applied Template Indicator */}
              {!templateMode && appliedTemplateName && (
                <div className="flex items-center gap-2 px-4 py-3 bg-blue-50 border border-blue-200 rounded-xl">
                  <FileText className="h-4 w-4 text-blue-600 flex-shrink-0" />
                  <span className="text-sm text-blue-800 font-medium flex-1">
                    Template: {appliedTemplateName}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setAppliedTemplateName(null);
                      setOffers([]);
                      setTitle('Proposal');
                      setExpiryOption('30');
                    }}
                    className="text-blue-400 hover:text-blue-600 transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              )}

              {/* Offers Section */}
              {(templateMode || selectedCustomer || appliedTemplateName) && (
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Offers *
                  </label>

                  {/* Existing Offers List */}
                  {offers.length > 0 && (
                    <div className="mb-6 space-y-4">
                      {offers.map((offer, index) => {
                        // Hide the offer summary if it's currently being edited
                        if (currentOffer === offer) {
                          return null;
                        }
                        return (
                        <div 
                          key={index} 
                          className={`bg-white rounded-xl p-4 border border-gray-300 transition-all ${
                            draggedOfferIndex === index ? 'opacity-50' : ''
                          } ${draggedOfferIndex !== null && draggedOfferIndex !== index ? 'border-blue-300' : ''}`}
                          draggable={offers.length > 1}
                          onDragStart={(e) => {
                            setDraggedOfferIndex(index);
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/html', index.toString());
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const draggedIndex = parseInt(e.dataTransfer.getData('text/html'));
                            const targetIndex = index;
                            
                            if (draggedIndex !== targetIndex && draggedIndex !== null) {
                              const newOffers = [...offers];
                              const [removed] = newOffers.splice(draggedIndex, 1);
                              newOffers.splice(targetIndex, 0, removed);
                              setOffers(newOffers);
                            }
                            setDraggedOfferIndex(null);
                          }}
                          onDragEnd={() => {
                            setDraggedOfferIndex(null);
                          }}
                        >
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center space-x-2 flex-1 min-w-0">
                              <div 
                                className="cursor-move text-gray-400 hover:text-gray-600 flex-shrink-0 touch-none"
                                onMouseDown={(e) => e.stopPropagation()}
                                onTouchStart={(e) => e.stopPropagation()}
                              >
                                <GripVertical className="h-5 w-5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <h4 className="text-sm font-bold text-gray-900 truncate">{offer.title}</h4>
                                {offer.deposit_percentage && offer.deposit_percentage > 0 && (
                                  <p className="text-xs text-purple-600 mt-0.5">
                                    Deposit: {offer.deposit_percentage}%
                                    {offer.save_card_on_deposit ? ' · Save card on file' : ''}
                                  </p>
                                )}
                                {vehiclesRequired && (
                                  offer.services.some(s => s.vehicleId === 'no-vehicle') ||
                                  (offer.products || []).some(p => p.vehicleId === 'no-vehicle')
                                ) && (
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      setAssignVehicleOfferIndex(index);
                                      setIsAssignVehicleModalOpen(true);
                                    }}
                                    className="mt-1 text-xs font-semibold text-red-600 hover:text-red-800 flex items-center gap-1 hover:underline transition-colors"
                                  >
                                    <Car className="h-3 w-3" />
                                    Assign Vehicle
                                  </button>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditOffer(offer);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newOffer = {
                                    title: `${offer.title} (Copy)`,
                                    deposit_percentage: offer.deposit_percentage || null,
                                    save_card_on_deposit: offer.save_card_on_deposit || null,
                                    show_customer_address: offer.show_customer_address || null,
                                    services: offer.services.map(service => ({
                                      ...service,
                                      id: service.id
                                    })),
                                    products: offer.products ? offer.products.map(product => ({
                                      ...product,
                                      id: product.id
                                    })) : []
                                  };
                                  setOffers(prev => [...prev, newOffer]);
                                }}
                                className="text-xs font-medium text-blue-600 hover:text-blue-800 px-2 py-1 rounded-lg hover:bg-blue-50 active:bg-blue-100 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Duplicate
                              </button>
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemoveOffer(index);
                                }}
                                className="text-xs font-medium text-red-600 hover:text-red-800 px-2 py-1 rounded-lg hover:bg-red-50 active:bg-red-100 transition-colors touch-manipulation"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                          <div className="space-y-2">
                            {/* Display regular services first */}
                            {offer.services
                              .filter(service => !service.isSelectable)
                              .map((service, serviceIndex) => (
                              <div key={`preview-regular-${serviceIndex}`} className="text-sm text-gray-600 font-medium">
                                <span>• {service.name} - {(() => {
                                  const compareAt = service.compare_at_price;
                                  const showCompare = compareAt != null && Number(compareAt) > service.price;
                                  const priceStr = (service.pricing_type === 'per_unit' || service.pricing_type === 'per_sqft')
                                    ? `${(service.quantity || 0).toLocaleString()} ${service.pricing_type === 'per_sqft' ? 'sqft' : 'units'} × $${service.unit_price} = $${service.price.toLocaleString()}`
                                    : `$${service.price.toLocaleString()}`;
                                  return (<>{showCompare && (<span className="line-through text-gray-400 mr-1">${Number(compareAt).toLocaleString()}</span>)}{priceStr}</>);
                                })()}{service.measurement_title ? ` 📐 ${service.measurement_title}` : ''}{service.vehicleName !== 'No vehicle' ? ` (${service.vehicleName})` : ''}</span>
                                {renderServicePhotoStrip(index, service)}
                              </div>
                            ))}
                            
                            {/* Show a divider if there are both regular and optional services */}
                            {offer.services.some(s => !s.isSelectable) && offer.services.some(s => s.isSelectable) && (
                              <div className="text-xs text-gray-500 pt-1 border-t border-purple-200/30 mt-1 font-bold">Optional Services</div>
                            )}
                            
                            {/* Display optional services next */}
                            {offer.services
                              .filter(service => service.isSelectable)
                              .map((service, serviceIndex) => (
                              <div key={`preview-optional-${serviceIndex}`} className="text-sm text-gray-500 font-medium">
                                <div className="flex items-center">
                                  <div className="w-3 h-3 mr-1 border border-purple-300 rounded-sm bg-purple-100"></div>
                                  <span className="text-gray-500">• {service.name} - ${service.price.toLocaleString()} (Optional)</span>
                                </div>
                                {renderServicePhotoStrip(index, service)}
                              </div>
                            ))}
                            
                            {/* Display products if any */}
                            {offer.products && offer.products.length > 0 && (
                              <>
                                {/* Show a divider if there are services above */}
                                {offer.services.length > 0 && (
                                  <div className="text-xs text-gray-500 pt-1 border-t border-green-200/30 mt-1 font-bold">Products</div>
                                )}
                                
                                {offer.products.map((product, productIndex) => (
                                  <div key={`preview-product-${productIndex}`} className="text-sm text-green-600 font-medium">
                                    <div className="flex items-center">
                                      <Package2 className="h-3 w-3 mr-1 text-green-500" />
                                      <span>• {product.name} (Qty: {product.quantity}) - ${(product.price * product.quantity).toFixed(2)}{product.vehicleName !== 'No vehicle' ? ` (${product.vehicleName})` : ''}</span>
                                    </div>
                                  </div>
                                ))}
                              </>
                            )}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  )}
                  
                  {!isOfferModalOpen && (
                    <button
                      type="button"
                      onClick={handleAddOffer}
                      className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all touch-manipulation flex items-center justify-center`}
                      style={{ minHeight: isMobile ? '44px' : '48px', WebkitTapHighlightColor: 'transparent' }}
                    >
                      <Plus className="h-5 w-5 mr-2" />
                      Create Offer
                    </button>
                  )}

                  {/* Offer Creation/Edit Section - Rendered as separate modal */}
                  {isOfferModalOpen && createPortal(
                    <div 
                      className="fixed inset-0 bg-black/50"
                      style={{ zIndex: baseZ + 5, backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)' }}
                    >
                      <div 
                        className={`bg-white transform transition-all duration-200 ${
                          isMobile 
                            ? 'relative w-full h-full'
                            : 'fixed inset-y-0 right-0 w-full md:w-[600px] shadow-2xl'
                        }`}
                        style={{
                          zIndex: baseZ + 6,
                          fontFamily: '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                          paddingTop: 'env(safe-area-inset-top, 0px)',
                          paddingBottom: 'env(safe-area-inset-bottom, 0px)',
                          paddingLeft: 'env(safe-area-inset-left, 0px)',
                          paddingRight: 'env(safe-area-inset-right, 0px)',
                        }}
                        onClick={e => e.stopPropagation()}
                      >
                        <div className="h-full flex flex-col overflow-hidden">
                          {/* Close Button */}
                          <button
                            onClick={() => {
                              setIsOfferModalOpen(false);
                              setOfferTitle('');
                              setRequireDeposit(false);
                              setOfferDepositPercentage('');
                              setSelectedDepositPercentage(null);
                              setSelectedServices([]);
                              setSelectedProducts([]);
                              setProductPhotos({});
                              setCurrentVehicle(null);
                              setCurrentOffer(null);
                            }}
                            className="absolute right-4 h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150 touch-manipulation z-10"
                            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)', WebkitTapHighlightColor: 'transparent' }}
                          >
                            <X className="h-5 w-5" />
                          </button>

                          {/* Header */}
                          <div className="bg-gray-50 border-b border-gray-200 px-6 py-6 text-center">
                            <h2 className="text-2xl font-bold text-gray-900" style={{ letterSpacing: '-0.02em' }}>
                              {currentOffer ? 'Edit Offer' : 'New Offer'}
                            </h2>
                          </div>

                          {/* Scrollable Content */}
                          <div 
                            ref={offerModalScrollRef}
                            className="flex-1 overflow-y-auto overflow-x-hidden" 
                            style={{ WebkitOverflowScrolling: 'touch', overflowAnchor: 'auto' }}
                          >
                            <div className={`${isMobile ? 'p-4' : 'p-6'} space-y-4`}>
                      <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Offer Title *
                        </label>
                        <input
                          type="text"
                          value={offerTitle}
                          onChange={(e) => setOfferTitle(e.target.value)}
                          placeholder="Enter offer title"
                          className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} text-base bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                          style={{ 
                            fontSize: '16px', 
                            minHeight: isMobile ? '44px' : '48px',
                            WebkitTapHighlightColor: 'transparent'
                          }}
                        />
                        <label className="mt-3 flex items-center gap-2.5 cursor-pointer select-none">
                          <input
                            type="checkbox"
                            checked={showCustomerAddress}
                            onChange={(e) => setShowCustomerAddress(e.target.checked)}
                            className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500/30"
                          />
                          <span className="text-sm text-gray-700">Show customer address</span>
                        </label>
                      </div>

                      {/* Vehicle Selection - Only show for Automotive (skip in template mode) */}
                      {(() => {
                        const isHomeServices = selectedBusiness?.industry === 'Home Services' || templateMode;
                        
                        // For Home Services / template mode, skip vehicle selection and show services directly
                        if (isHomeServices) {
                          // If no services yet, show "Add Service" button
                          if (selectedServices.length === 0 && selectedProducts.length === 0) {
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  setCurrentVehicle(null);
                                  setIsSelectServiceModalOpen(true);
                                }}
                                className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} border-2 border-dashed border-blue-300 rounded-xl text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 active:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all touch-manipulation`}
                                style={{ 
                                  minHeight: isMobile ? '44px' : '48px',
                                  WebkitTapHighlightColor: 'transparent'
                                }}
                              >
                                <Plus className="h-4 w-4 inline mr-2" />
                                Add Service
                              </button>
                            );
                          }
                          // If services exist, show them grouped (will be handled below)
                          return null;
                        }
                        
                        // For Automotive, show vehicle selection
                        if (!currentVehicle) {
                          return (
                            <button
                              type="button"
                              onClick={() => setIsSelectVehicleModalOpen(true)} 
                              className={`w-full ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'} border-2 border-dashed border-gray-300 rounded-xl text-sm font-medium text-gray-700 hover:text-gray-900 hover:border-gray-400 hover:bg-gray-50 active:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-all touch-manipulation`}
                              style={{ 
                                minHeight: isMobile ? '44px' : '48px',
                                WebkitTapHighlightColor: 'transparent'
                              }}
                            >
                              Select Vehicle
                            </button>
                          );
                        }
                        return null;
                      })()}
                      
                      {/* Services and Products Display - Show if vehicle selected OR Home Services/template with services */}
                      {(() => {
                        const isHomeServices = selectedBusiness?.industry === 'Home Services' || templateMode;
                        const hasServicesOrProducts = selectedServices.length > 0 || selectedProducts.length > 0;
                        
                        // Show if: (Automotive with vehicle) OR (Home Services/template with services/products)
                        if ((!isHomeServices && currentVehicle) || (isHomeServices && hasServicesOrProducts)) {
                          return (
                        <div className="space-y-4">
                          {/* Group services by vehicle */}
                          {Object.entries(
                            selectedServices.reduce<Record<string, typeof selectedServices>>((acc, service) => {
                              if (!acc[service.vehicleId]) {
                                acc[service.vehicleId] = [];
                              }
                              acc[service.vehicleId].push(service);
                              return acc;
                            }, {})
                          ).map(([vehicleId, vehicleServices]) => {
                            const isNoVehicle = vehicleId === 'no-vehicle';
                            
                            return (
                            <div key={vehicleId} className="bg-white rounded-xl border border-gray-300 p-4">
                              {/* Vehicle Header - Only show for Automotive or if vehicle exists */}
                              {!isNoVehicle && (
                                <div className="flex items-center justify-between mb-4">
                                  <div className="flex items-center space-x-3">
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
                                      <Car className="h-6 w-6 text-white" />
                                    </div>
                                    <div>
                                      <div className="text-sm font-semibold text-gray-900">
                                        {vehicleServices[0].vehicleName}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}

                              {/* Services for this vehicle */}
                              <div className="space-y-4">
                                {/* Display regular services first */}
                                {vehicleServices
                                  .filter(service => !service.isSelectable)
                                  .map((service, index) => (
                                  <div key={`regular-${index}`} className="bg-white rounded-xl p-3 border border-gray-300">
                                    {/* Service Title + Delete Row */}
                                    <div className="flex items-start justify-between gap-2 mb-2">
                                      <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 mb-1">
                                          Service Title
                                        </label>
                                        <input
                                          type="text"
                                          value={service.name}
                                          onChange={(e) => handleServiceNameChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          )}
                                          className="w-full px-3 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                          placeholder="Enter service name"
                                        />
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                        className="mt-5 p-2 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50/80 transition-all flex-shrink-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>

                                    {/* Description - Full Width */}
                                    <div className="mb-2">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Description
                                      </label>
                                      <textarea
                                        ref={(el) => {
                                          if (el) {
                                            el.style.height = 'auto';
                                            el.style.height = Math.max(120, el.scrollHeight) + 'px';
                                          }
                                        }}
                                        value={service.description}
                                        onChange={(e) => {
                                          handleServiceDescriptionChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          );
                                          // Auto-grow on input
                                          e.target.style.height = 'auto';
                                          e.target.style.height = Math.max(120, e.target.scrollHeight) + 'px';
                                        }}
                                        className="w-full px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-y font-medium"
                                        placeholder="Enter service description"
                                        rows={5}
                                        style={{ minHeight: '120px' }}
                                      />
                                    </div>

                                    {/* Duration, Price, Discount Row */}
                                    <div className="space-y-2">
                                      {(service.pricing_type === 'per_unit' || service.pricing_type === 'per_sqft') ? (
                                        <>
                                          {/* Per-unit/sqft pricing controls */}
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded">
                                              {service.pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
                                            </span>
                                            <span className="text-xs text-gray-500 font-medium">
                                              ${service.unit_price?.toFixed(2)}/{service.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} &middot; {service.duration_per_unit} hrs/{service.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                            </span>
                                          </div>

                                          {/* Linked measurement display */}
                                          {service.measurement_title && (
                                            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg mb-1">
                                              <Ruler className="h-3 w-3 text-green-600 flex-shrink-0" />
                                              <span className="text-xs font-medium text-green-700 truncate">{service.measurement_title}</span>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                    ...s, measurement_index: undefined, measurement_title: undefined, quantity: 0, price: 0, duration_minutes: 0
                                                  } : s));
                                                }}
                                                className="ml-auto text-green-400 hover:text-red-500 flex-shrink-0"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </div>
                                          )}

                                          {/* Quantity Input + Select Measurement */}
                                          <div className="flex items-center gap-3">
                                            <div>
                                              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                                {service.pricing_type === 'per_sqft' ? 'Square Feet' : 'Units'}
                                              </label>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={service.quantity || ''}
                                                onChange={(e) => {
                                                  const qty = parseFloat(e.target.value) || 0;
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  const computedPrice = Math.round(qty * (service.unit_price || 0) * 100) / 100;
                                                  const updates: Partial<ServiceOption> = {
                                                    quantity: qty,
                                                    price: computedPrice,
                                                    measurement_index: undefined,
                                                    measurement_title: undefined
                                                  };
                                                  if (!service._durationOverridden) {
                                                    updates.duration_minutes = Math.round(qty * (service.duration_per_unit || 0) * 60);
                                                  }
                                                  setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? { ...s, ...updates } : s));
                                                }}
                                                placeholder="0"
                                                className="w-28 px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            <div className="pt-4">
                                              <span className="text-lg font-bold text-gray-900">
                                                ${service.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Editable hours */}
                                          <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((service.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                  ...s, duration_minutes: Math.round(hours * 60), _durationOverridden: true
                                                } : s));
                                              }}
                                              className="w-20 px-2 py-1 text-xs font-medium text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>

                                          {/* Select Measurement Button */}
                                          {service.pricing_type === 'per_sqft' && measurements.length > 0 && !service.measurement_title && (
                                            <div className="relative mt-1">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  setMeasurementPickerServiceIndex(serviceIndex);
                                                }}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors touch-manipulation"
                                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                              >
                                                <Globe className="h-3 w-3" />
                                                Select Measurement
                                              </button>
                                            </div>
                                          )}

                                          {/* Measurement Picker Dropdown */}
                                          {measurementPickerServiceIndex === selectedServices.findIndex(s => s === service) && (
                                            <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Select a measurement</span>
                                              </div>
                                              {measurements.map((m, mIdx) => (
                                                <button
                                                  key={mIdx}
                                                  type="button"
                                                  onClick={() => {
                                                    const serviceIndex = selectedServices.findIndex(s => s === service);
                                                    const qty = m.area_sqft;
                                                    const computedPrice = Math.round(qty * (service.unit_price || 0) * 100) / 100;
                                                    const updates: Partial<ServiceOption> = {
                                                      quantity: qty,
                                                      price: computedPrice,
                                                      measurement_index: mIdx,
                                                      measurement_title: m.title
                                                    };
                                                    if (!service._durationOverridden) {
                                                      updates.duration_minutes = Math.round(qty * (service.duration_per_unit || 0) * 60);
                                                    }
                                                    setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? { ...s, ...updates } : s));
                                                    setMeasurementPickerServiceIndex(null);
                                                  }}
                                                  className="w-full text-left px-3 py-2.5 hover:bg-blue-50 active:bg-blue-100 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                                                >
                                                  <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Ruler className="h-3.5 w-3.5 text-green-600" />
                                                  </div>
                                                  <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-gray-900 truncate">{m.title}</div>
                                                    <div className="text-[11px] text-gray-500">{m.area_sqft.toLocaleString()} sq ft</div>
                                                  </div>
                                                </button>
                                              ))}
                                              <button
                                                type="button"
                                                onClick={() => setMeasurementPickerServiceIndex(null)}
                                                className="w-full text-center px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 font-medium border-t border-gray-200"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((service.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                  ...s, duration_minutes: Math.round(hours * 60)
                                                } : s));
                                              }}
                                              className="w-20 px-2 py-1 text-xs font-medium text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>
                                          
                                          {/* Price Input */}
                                          <div className="flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 font-bold">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            {service.discountValue && service.discountValue > 0 && (
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-500 font-medium">→</span>
                                                <span className="text-sm font-bold text-green-600">
                                                  ${service.discountedPrice?.toLocaleString()} 
                                                  <span className="text-xs text-gray-500 ml-1">
                                                    (after {service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`} discount)
                                                  </span>
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}

                                      {/* Discount Controls */}
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600 font-medium">Discount:</span>
                                        <select
                                          value={service.discountType || 'percentage'}
                                          onChange={(e) => {
                                            const serviceIndex = selectedServices.findIndex(s => s === service);
                                            handleServiceDiscountChange(
                                              serviceIndex,
                                              e.target.value as 'percentage' | 'dollar',
                                              service.discountValue || 0
                                            );
                                          }}
                                          className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        >
                                          <option value="percentage">%</option>
                                          <option value="dollar">$</option>
                                        </select>
                                        <input
                                          type="number"
                                          value={service.discountValue || 0}
                                          onChange={(e) => {
                                            const serviceIndex = selectedServices.findIndex(s => s === service);
                                            handleServiceDiscountChange(
                                              serviceIndex,
                                              service.discountType || 'percentage',
                                              parseFloat(e.target.value) || 0
                                            );
                                          }}
                                          placeholder="0"
                                          className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                        {service.discountValue && service.discountValue > 0 && (
                                          <span className="text-xs text-green-600 font-medium">
                                            -{service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Display selectable (optional) services next */}
                                {vehicleServices.filter(service => service.isSelectable).length > 0 && (
                                  <div className="border-t border-purple-200/30 pt-3 mt-3 mb-2">
                                    <div className="text-center text-sm text-purple-700 font-bold bg-purple-50/80 py-2 rounded-xl border border-purple-200/30">
                                      Optional Add-on Services
                                    </div>
                                  </div>
                                )}
                                
                                {vehicleServices
                                  .filter(service => service.isSelectable)
                                  .map((service, index) => (
                                  <div key={`optional-${index}`} className="bg-white/60 backdrop-blur-sm rounded-2xl p-3 border border-purple-200/20 opacity-80"
                                       style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                                    {/* Optional Add-on Badge */}
                                    <div className="mb-2 flex items-center justify-between">
                                      <div className="flex items-center">
                                        <div className="w-10 h-5 bg-purple-200 rounded-full p-1 mr-2">
                                          <div className="bg-purple-500 w-3 h-3 rounded-full"></div>
                                        </div>
                                        <span className="text-xs text-purple-600 font-bold">Optional Add-on</span>
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => handleRemoveService(selectedServices.findIndex(s => s === service))}
                                        className="p-2 text-red-500 hover:text-red-700 rounded-xl hover:bg-red-50/80 transition-all flex-shrink-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </button>
                                    </div>

                                    {/* Service Title - Full Width */}
                                    <div className="mb-2">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Service Title
                                      </label>
                                      <input
                                        type="text"
                                        value={service.name}
                                        onChange={(e) => handleServiceNameChange(
                                          selectedServices.findIndex(s => s === service),
                                          e.target.value
                                        )}
                                        className="w-full px-3 py-2 text-sm font-semibold text-gray-900 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
                                        placeholder="Enter service name"
                                      />
                                    </div>

                                    {/* Description - Full Width */}
                                    <div className="mb-2">
                                      <label className="block text-xs font-medium text-gray-500 mb-1">
                                        Description
                                      </label>
                                      <textarea
                                        ref={(el) => {
                                          if (el) {
                                            el.style.height = 'auto';
                                            el.style.height = Math.max(120, el.scrollHeight) + 'px';
                                          }
                                        }}
                                        value={service.description}
                                        onChange={(e) => {
                                          handleServiceDescriptionChange(
                                            selectedServices.findIndex(s => s === service),
                                            e.target.value
                                          );
                                          // Auto-grow on input
                                          e.target.style.height = 'auto';
                                          e.target.style.height = Math.max(120, e.target.scrollHeight) + 'px';
                                        }}
                                        className="w-full px-3 py-2 text-sm text-gray-600 bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all resize-y font-medium"
                                        placeholder="Enter service description"
                                        rows={5}
                                        style={{ minHeight: '120px' }}
                                      />
                                    </div>

                                    {/* Duration, Price, Discount */}
                                    <div className="space-y-2">
                                      {(service.pricing_type === 'per_unit' || service.pricing_type === 'per_sqft') ? (
                                        <>
                                          <div className="flex items-center gap-2 mb-1">
                                            <span className="text-[10px] font-semibold text-purple-600 bg-purple-50 px-1.5 py-0.5 rounded">
                                              {service.pricing_type === 'per_sqft' ? 'PER SQ FT' : 'PER UNIT'}
                                            </span>
                                            <span className="text-xs text-gray-500 font-medium">
                                              ${service.unit_price?.toFixed(2)}/{service.pricing_type === 'per_sqft' ? 'sqft' : 'unit'} &middot; {service.duration_per_unit} hrs/{service.pricing_type === 'per_sqft' ? 'sqft' : 'unit'}
                                            </span>
                                          </div>

                                          {service.measurement_title && (
                                            <div className="flex items-center gap-2 px-2.5 py-1.5 bg-green-50 border border-green-200 rounded-lg mb-1">
                                              <Ruler className="h-3 w-3 text-green-600 flex-shrink-0" />
                                              <span className="text-xs font-medium text-green-700 truncate">{service.measurement_title}</span>
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                    ...s, measurement_index: undefined, measurement_title: undefined, quantity: 0, price: 0, duration_minutes: 0
                                                  } : s));
                                                }}
                                                className="ml-auto text-green-400 hover:text-red-500 flex-shrink-0"
                                              >
                                                <X className="h-3 w-3" />
                                              </button>
                                            </div>
                                          )}

                                          <div className="flex items-center gap-3">
                                            <div>
                                              <label className="block text-[10px] font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                                {service.pricing_type === 'per_sqft' ? 'Square Feet' : 'Units'}
                                              </label>
                                              <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={service.quantity || ''}
                                                onChange={(e) => {
                                                  const qty = parseFloat(e.target.value) || 0;
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  const computedPrice = Math.round(qty * (service.unit_price || 0) * 100) / 100;
                                                  const updates: Partial<ServiceOption> = {
                                                    quantity: qty,
                                                    price: computedPrice,
                                                    measurement_index: undefined,
                                                    measurement_title: undefined
                                                  };
                                                  if (!service._durationOverridden) {
                                                    updates.duration_minutes = Math.round(qty * (service.duration_per_unit || 0) * 60);
                                                  }
                                                  setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? { ...s, ...updates } : s));
                                                }}
                                                placeholder="0"
                                                className="w-28 px-3 py-1.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            <div className="pt-4">
                                              <span className="text-lg font-bold text-gray-900">
                                                ${service.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                              </span>
                                            </div>
                                          </div>

                                          {/* Editable hours */}
                                          <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((service.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                  ...s, duration_minutes: Math.round(hours * 60), _durationOverridden: true
                                                } : s));
                                              }}
                                              className="w-20 px-2 py-1 text-xs font-medium text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>

                                          {service.pricing_type === 'per_sqft' && measurements.length > 0 && !service.measurement_title && (
                                            <div className="relative mt-1">
                                              <button
                                                type="button"
                                                onClick={() => {
                                                  const serviceIndex = selectedServices.findIndex(s => s === service);
                                                  setMeasurementPickerServiceIndex(serviceIndex);
                                                }}
                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-green-700 bg-green-50 hover:bg-green-100 border border-green-200 rounded-lg transition-colors touch-manipulation"
                                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                              >
                                                <Globe className="h-3 w-3" />
                                                Select Measurement
                                              </button>
                                            </div>
                                          )}

                                          {measurementPickerServiceIndex === selectedServices.findIndex(s => s === service) && (
                                            <div className="mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                              <div className="px-3 py-2 bg-gray-50 border-b border-gray-200">
                                                <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Select a measurement</span>
                                              </div>
                                              {measurements.map((m, mIdx) => (
                                                <button
                                                  key={mIdx}
                                                  type="button"
                                                  onClick={() => {
                                                    const serviceIndex = selectedServices.findIndex(s => s === service);
                                                    const qty = m.area_sqft;
                                                    const computedPrice = Math.round(qty * (service.unit_price || 0) * 100) / 100;
                                                    const updates: Partial<ServiceOption> = {
                                                      quantity: qty,
                                                      price: computedPrice,
                                                      measurement_index: mIdx,
                                                      measurement_title: m.title
                                                    };
                                                    if (!service._durationOverridden) {
                                                      updates.duration_minutes = Math.round(qty * (service.duration_per_unit || 0) * 60);
                                                    }
                                                    setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? { ...s, ...updates } : s));
                                                    setMeasurementPickerServiceIndex(null);
                                                  }}
                                                  className="w-full text-left px-3 py-2.5 hover:bg-blue-50 active:bg-blue-100 transition-colors flex items-center gap-3 border-b border-gray-100 last:border-b-0"
                                                >
                                                  <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                                    <Ruler className="h-3.5 w-3.5 text-green-600" />
                                                  </div>
                                                  <div className="min-w-0 flex-1">
                                                    <div className="text-sm font-semibold text-gray-900 truncate">{m.title}</div>
                                                    <div className="text-[11px] text-gray-500">{m.area_sqft.toLocaleString()} sq ft</div>
                                                  </div>
                                                </button>
                                              ))}
                                              <button
                                                type="button"
                                                onClick={() => setMeasurementPickerServiceIndex(null)}
                                                className="w-full text-center px-3 py-2 text-xs text-gray-500 hover:bg-gray-50 font-medium border-t border-gray-200"
                                              >
                                                Cancel
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      ) : (
                                        <>
                                          <div className="flex items-center gap-1.5">
                                            <Clock className="h-3.5 w-3.5 text-gray-400" />
                                            <input
                                              type="number"
                                              value={parseFloat(((service.duration_minutes || 0) / 60).toFixed(2))}
                                              onChange={(e) => {
                                                const hours = parseFloat(e.target.value) || 0;
                                                const serviceIndex = selectedServices.findIndex(s => s === service);
                                                setSelectedServices(prev => prev.map((s, i) => i === serviceIndex ? {
                                                  ...s, duration_minutes: Math.round(hours * 60)
                                                } : s));
                                              }}
                                              className="w-20 px-2 py-1 text-xs font-medium text-gray-900 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              step="0.25"
                                              min="0"
                                            />
                                            <span className="text-xs text-gray-400">hrs</span>
                                          </div>
                                          
                                          {/* Price Input */}
                                          <div className="flex items-center space-x-2">
                                            <div className="relative">
                                              <span className="absolute inset-y-0 left-0 pl-2 flex items-center text-gray-500 font-bold">$</span>
                                              <input
                                                type="number"
                                                value={service.price}
                                                onChange={(e) => handlePriceChange(
                                                  selectedServices.findIndex(s => s === service),
                                                  parseFloat(e.target.value) || 0
                                                )}
                                                className="pl-6 pr-2 py-1 w-32 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                              />
                                            </div>
                                            {service.discountValue && service.discountValue > 0 && (
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-500 font-medium">→</span>
                                                <span className="text-sm font-bold text-green-600">
                                                  ${service.discountedPrice?.toLocaleString()} 
                                                  <span className="text-xs text-gray-500 ml-1">
                                                    (after {service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`} discount)
                                                  </span>
                                                </span>
                                              </div>
                                            )}
                                          </div>
                                        </>
                                      )}

                                      {/* Discount Controls */}
                                      <div className="flex items-center space-x-2">
                                        <span className="text-xs text-gray-600 font-medium">Discount:</span>
                                        <select
                                          value={service.discountType || 'percentage'}
                                          onChange={(e) => {
                                            const serviceIndex = selectedServices.findIndex(s => s === service);
                                            handleServiceDiscountChange(
                                              serviceIndex,
                                              e.target.value as 'percentage' | 'dollar',
                                              service.discountValue || 0
                                            );
                                          }}
                                          className="text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        >
                                          <option value="percentage">%</option>
                                          <option value="dollar">$</option>
                                        </select>
                                        <input
                                          type="number"
                                          value={service.discountValue || 0}
                                          onChange={(e) => {
                                            const serviceIndex = selectedServices.findIndex(s => s === service);
                                            handleServiceDiscountChange(
                                              serviceIndex,
                                              service.discountType || 'percentage',
                                              parseFloat(e.target.value) || 0
                                            );
                                          }}
                                          placeholder="0"
                                          className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                        {service.discountValue && service.discountValue > 0 && (
                                          <span className="text-xs text-green-600 font-medium">
                                            -{service.discountType === 'percentage' ? `${service.discountValue}%` : `$${service.discountValue}`}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                ))}

                                {/* Display products for this vehicle */}
                                {selectedProducts.filter(product => product.vehicleId === vehicleId).length > 0 && (
                                  <>
                                    <div className="border-t border-green-200/30 pt-3 mt-3 mb-2">
                                      <div className="text-center text-sm text-green-700 font-bold bg-green-50/80 py-2 rounded-xl border border-green-200/30">
                                        Products
                                      </div>
                                    </div>
                                    
                                    {selectedProducts
                                      .filter(product => product.vehicleId === vehicleId)
                                      .map((product, index) => (
                                      <div key={`product-${index}`} className="bg-green-50/60 backdrop-blur-sm rounded-2xl p-3 border border-green-200/20 relative">
                                        {/* Delete button positioned absolutely in top-right */}
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveProduct(selectedProducts.findIndex(p => p === product))}
                                          className="absolute top-2 right-2 p-1.5 text-red-500 hover:text-red-700 rounded-lg hover:bg-red-50/80 transition-all"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                        
                                        <div className="flex items-start space-x-3 pr-8">
                                          {/* Product Icon/Photo */}
                                          <div className="h-12 w-12 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                                            {productPhotos[product.id] ? (
                                              <img
                                                src={productPhotos[product.id]}
                                                alt={product.name}
                                                className="h-10 w-10 rounded-lg object-cover"
                                              />
                                            ) : (
                                              <Package2 className="h-6 w-6 text-green-600" />
                                            )}
                                          </div>
                                          
                                          <div className="flex-1">
                                            <div className="text-sm font-bold text-gray-900 mb-1">
                                              {product.name}
                                            </div>
                                            <div className="text-xs text-gray-500 mb-2">
                                              Category: {product.category_name}
                                            </div>
                                            
                                            {/* Quantity and Price */}
                                            <div className="flex items-center space-x-4 mb-2">
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600 font-medium">Qty:</span>
                                                <input
                                                  type="number"
                                                  value={product.quantity}
                                                  onChange={(e) => {
                                                    const newQuantity = parseInt(e.target.value) || 1;
                                                    setSelectedProducts(prev => prev.map((p) => 
                                                      selectedProducts.findIndex(sp => sp === p) === selectedProducts.findIndex(sp => sp === product) 
                                                        ? { ...p, quantity: newQuantity } 
                                                        : p
                                                    ));
                                                  }}
                                                  min="1"
                                                  className="w-16 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                                                />
                                              </div>
                                              
                                              <div className="flex items-center space-x-2">
                                                <span className="text-xs text-gray-600 font-medium">Price:</span>
                                                <input
                                                  type="number"
                                                  value={product.price}
                                                  onChange={(e) => {
                                                    const newPrice = parseFloat(e.target.value) || 0;
                                                    setSelectedProducts(prev => prev.map((p) => 
                                                      selectedProducts.findIndex(sp => sp === p) === selectedProducts.findIndex(sp => sp === product) 
                                                        ? { ...p, price: newPrice } 
                                                        : p
                                                    ));
                                                  }}
                                                  step="0.01"
                                                  className="w-20 text-xs border border-gray-300 rounded-lg px-2 py-1 focus:outline-none focus:ring-1 focus:ring-green-500"
                                                />
                                              </div>
                                            </div>
                                            
                                            {/* Total on separate line */}
                                            <div className="text-xs text-green-600 font-bold">
                                              Total: ${(product.price * product.quantity).toFixed(2)}
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    ))}
                                  </>
                                )}
                              </div>

                              {/* Add Service (required), Add Optional Service, Add Product */}
                              <div className="mt-4 space-y-2">
                                <button
                                  type="button"
                                  onClick={() => {
                                    const isHomeServices = selectedBusiness?.industry === 'Home Services';
                                    if (isHomeServices) {
                                      setCurrentVehicle(null);
                                      localStorage.removeItem('adding_selectable_service');
                                      setIsSelectServiceModalOpen(true);
                                    } else {
                                      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                      if (vehicle) {
                                        setCurrentVehicle(vehicle);
                                        localStorage.removeItem('adding_selectable_service');
                                        setIsSelectServiceModalOpen(true);
                                      }
                                    }
                                  }}
                                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Service
                                </button>
                                <button
                                  type="button"
                                  onClick={() => {
                                    const isHomeServices = selectedBusiness?.industry === 'Home Services';
                                    if (isHomeServices) {
                                      setCurrentVehicle(null);
                                      localStorage.setItem('adding_selectable_service', 'true');
                                      setIsSelectServiceModalOpen(true);
                                    } else {
                                      const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                      if (vehicle) {
                                        setCurrentVehicle(vehicle);
                                        localStorage.setItem('adding_selectable_service', 'true');
                                        setIsSelectServiceModalOpen(true);
                                      }
                                    }
                                  }}
                                  className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-200 rounded-lg text-sm font-medium text-blue-500 hover:text-blue-700 hover:border-blue-300 hover:bg-blue-50/70 focus:outline-none focus:ring-2 focus:ring-blue-400 transition-colors"
                                >
                                  <Plus className="h-4 w-4 mr-2" />
                                  Add Optional Service
                                </button>
                                {/* Add Product Button - Only show if Inventory app is installed and setting is enabled */}
                                {isInventoryAppInstalled && showInventoryInBookings && (
                                  <button
                                    type="button"
                                    onClick={() => {
                                      const isHomeServices = selectedBusiness?.industry === 'Home Services';
                                      if (isHomeServices) {
                                        // For Home Services, no vehicle needed
                                        setCurrentVehicle(null);
                                        setIsSelectInventoryItemModalOpen(true);
                                      } else {
                                        // For Automotive, require vehicle
                                        const vehicle = selectedCustomer?.vehicles?.find(v => v.id === vehicleId);
                                        if (vehicle) {
                                          setCurrentVehicle(vehicle);
                                          setIsSelectInventoryItemModalOpen(true);
                                        }
                                      }
                                    }}
                                    className="w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-green-300 rounded-lg text-sm font-medium text-green-600 hover:text-green-800 hover:border-green-400 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                  >
                                    <Package2 className="h-4 w-4 mr-2" />
                                    Add Product
                                  </button>
                                )}
                              </div>
                            </div>
                          );
                          })}

                          {/* Add Another Vehicle Button - Only for Automotive */}
                          {selectedBusiness?.industry !== 'Home Services' && (
                            <button
                              type="button"
                              onClick={() => setIsSelectVehicleModalOpen(true)}
                              className="mt-4 w-full flex items-center justify-center px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg text-sm font-medium text-blue-600 hover:text-blue-800 hover:border-blue-400 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
                            >
                              <Plus className="h-5 w-5 mr-2" />
                              Add Another Vehicle
                            </button>
                          )}
                        </div>
                      );
                        }
                        return null;
                      })()}



                      {/* Pricing Summary */}
                      {(selectedServices.length > 0 || selectedProducts.length > 0) && (
                        <div className="mt-6 pt-6 border-t border-purple-200/30">
                          <h4 className="text-sm font-bold text-gray-900 mb-4">Pricing Summary</h4>
                          <div className="bg-white/60 backdrop-blur-sm rounded-2xl p-4 border border-purple-200/20"
                               style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}>
                            <div className="space-y-2">
                              {(() => {
                                // Calculate service totals
                                const serviceSubtotal = selectedServices.reduce((sum, service) => sum + service.price, 0);
                                const serviceDiscount = selectedServices.reduce((sum, service) => sum + (service.discountAmount || 0), 0);
                                
                                // Calculate product totals
                                const productSubtotal = selectedProducts.reduce((sum, product) => sum + (product.price * product.quantity), 0);
                                const productDiscount = selectedProducts.reduce((sum, product) => sum + (product.discountAmount || 0), 0);
                                
                                // Combined totals
                                const originalSubtotal = serviceSubtotal + productSubtotal;
                                const totalDiscount = serviceDiscount + productDiscount;
                                const currentSubtotal = originalSubtotal - totalDiscount;
                                const businessTax = Math.round((currentSubtotal * combinedBusinessRate) * 100) / 100;

                                const additionalTaxMap = new Map<string, { name: string; rate: number; amount: number }>();
                                for (const atx of serviceAdditionalTaxes) {
                                  const svc = selectedServices.find(s => s.id === atx.service_id);
                                  if (!svc) continue;
                                  const svcNet = svc.price - (svc.discountAmount || 0);
                                  const amt = svcNet * atx.rate / 100;
                                  const key = `${atx.name}_${atx.rate}`;
                                  if (additionalTaxMap.has(key)) {
                                    additionalTaxMap.get(key)!.amount += amt;
                                  } else {
                                    additionalTaxMap.set(key, { name: atx.name, rate: atx.rate, amount: amt });
                                  }
                                }
                                const additionalTaxLines = Array.from(additionalTaxMap.values()).map(t => ({
                                  ...t, amount: Math.round(t.amount * 100) / 100
                                }));
                                const additionalTaxTotal = additionalTaxLines.reduce((s, t) => s + t.amount, 0);
                                const totalTax = businessTax + additionalTaxTotal;
                                const total = Math.round((currentSubtotal + totalTax) * 100) / 100;

                                return (
                                  <>
                                    <div className="flex justify-between text-sm text-gray-600 font-medium">
                                      <span>Original Subtotal</span>
                                      <span>${originalSubtotal.toLocaleString()}</span>
                                    </div>
                                    {totalDiscount > 0 && (
                                      <div className="flex justify-between text-sm text-green-600 font-medium">
                                        <span>Total Discounts Applied</span>
                                        <span>- ${totalDiscount.toLocaleString()}</span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-sm text-gray-900 font-medium">
                                      <span>Subtotal After Discounts</span>
                                      <span>${currentSubtotal.toLocaleString()}</span>
                                    </div>
                                    {businessTaxRates.map((rate, ri) => (
                                      <div key={ri} className="flex justify-between text-sm text-gray-600 font-medium">
                                        <span>{rate.name} ({rate.rate}%)</span>
                                        <span>+ ${(Math.round((currentSubtotal * rate.rate / 100) * 100) / 100).toLocaleString()}</span>
                                      </div>
                                    ))}
                                    {additionalTaxLines.map((line, li) => (
                                      <div key={`addl-${li}`} className="flex justify-between text-sm text-gray-600 font-medium">
                                        <span>{line.name} ({line.rate}%)</span>
                                        <span>+ ${line.amount.toLocaleString()}</span>
                                      </div>
                                    ))}
                                    <div className="flex justify-between text-sm font-bold text-gray-900 pt-2 border-t border-purple-200/30">
                                      <span>Total</span>
                                      <span>${total.toLocaleString()}</span>
                                    </div>
                                    {requireDeposit && offerDepositPercentage && parseFloat(offerDepositPercentage) > 0 && (() => {
                                      const depositPercentage = parseFloat(offerDepositPercentage);
                                      const depositAmount = Math.round((total * depositPercentage / 100) * 100) / 100;
                                      const remainingAfterDeposit = total - depositAmount;
                                      
                                      return (
                                        <>
                                          <div className="flex justify-between text-sm text-purple-600 font-medium pt-2 border-t border-purple-200/30">
                                            <span>Deposit Required ({depositPercentage}%)</span>
                                            <span>${depositAmount.toLocaleString()}</span>
                                          </div>
                                          <div className="flex justify-between text-xs text-gray-500 font-medium">
                                            <span>Remaining After Deposit</span>
                                            <span>${remainingAfterDeposit.toLocaleString()}</span>
                                          </div>
                                        </>
                                      );
                                    })()}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Require Deposit - Blue toggle with deposit amount on the right when on */}
                      {selectedBusiness?.stripe_connect_account_id && selectedBusiness?.stripe_connect_charges_enabled && (selectedServices.length > 0 || selectedProducts.length > 0) && (
                        <div className="mt-4">
                          <div className="flex flex-wrap items-center justify-between gap-3">
                            <label className="relative inline-flex items-center gap-3 cursor-pointer flex-shrink-0">
                              <input
                                type="checkbox"
                                checked={requireDeposit}
                                onChange={(e) => {
                                  savedOfferScrollTopRef.current = offerModalScrollRef.current?.scrollTop ?? 0;
                                  didToggleDepositRef.current = true;
                                  setRequireDeposit(e.target.checked);
                                  if (!e.target.checked) {
                                    setOfferDepositPercentage('');
                                    setSelectedDepositPercentage(null);
                                    setSaveCardOnDeposit(false);
                                  } else {
                                    setSaveCardOnDeposit(true);
                                  }
                                }}
                                className="sr-only peer"
                              />
                              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 shrink-0" />
                              <span className="text-sm font-medium text-gray-700">
                                Require deposit
                              </span>
                            </label>
                            {/* Deposit amount / percentage - to the right of the toggle when on */}
                            {requireDeposit && (
                              <div className="flex flex-wrap items-center gap-2">
                                <div className="relative flex items-center">
                                  <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    value={offerDepositPercentage}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      const numValue = parseFloat(value);
                                      if (value === '' || (numValue >= 0 && numValue <= 100)) {
                                        setOfferDepositPercentage(value);
                                        setSelectedDepositPercentage(null);
                                      }
                                    }}
                                    placeholder="0"
                                    className={`w-16 pl-2.5 pr-7 ${isMobile ? 'py-2' : 'py-2'} text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all touch-manipulation`}
                                    style={{ WebkitTapHighlightColor: 'transparent' }}
                                  />
                                  <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-sm">%</span>
                                </div>
                                <div className="flex gap-1">
                                  {(['25', '50', '75', 'full'] as const).map((pct) => {
                                    const value = pct === 'full' ? '100' : pct;
                                    const isSelected = (pct === 'full' && selectedDepositPercentage === 'full') || (pct !== 'full' && selectedDepositPercentage === pct);
                                    return (
                                      <button
                                        key={pct}
                                        type="button"
                                        onClick={() => {
                                          setOfferDepositPercentage(value);
                                          setSelectedDepositPercentage(pct === 'full' ? 'full' : pct);
                                        }}
                                        className={`px-2.5 py-1 text-xs font-medium rounded-lg transition-colors ${
                                          isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                                        }`}
                                      >
                                        {pct === 'full' ? 'Full' : `${pct}%`}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                          {requireDeposit && (
                            <p className="text-xs text-gray-500 mt-2">
                              Deposit amount is calculated from the final offer total (including any optional services the customer selects).
                            </p>
                          )}
                          {requireDeposit &&
                            selectedCustomer?.id &&
                            selectedBusiness?.stripe_connect_account_id &&
                            selectedBusiness?.stripe_connect_charges_enabled && (
                              <div className="mt-3 flex items-start gap-3">
                                <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-0.5">
                                  <input
                                    type="checkbox"
                                    checked={saveCardOnDeposit}
                                    onChange={(e) => setSaveCardOnDeposit(e.target.checked)}
                                    className="sr-only peer"
                                  />
                                  <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600 shrink-0" />
                                </label>
                                <div>
                                  <span className="text-sm font-medium text-gray-800">Save card on file</span>
                                  <p className="text-xs text-gray-500 mt-0.5">
                                    When they pay the deposit online, store their card for later. Replaces any existing saved card for this customer.
                                  </p>
                                </div>
                              </div>
                            )}
                        </div>
                      )}

                            </div>
                          </div>

                          {/* Fixed Footer with Action Buttons */}
                          <div className="border-t border-gray-200 bg-white" style={{
                            paddingTop: isMobile ? '12px' : '16px',
                            paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '16px',
                            paddingLeft: isMobile ? '16px' : '24px',
                            paddingRight: isMobile ? '16px' : '24px'
                          }}>
                            <div className="flex justify-end space-x-3">
                              <button
                                type="button"
                                onClick={() => {
                                  setIsOfferModalOpen(false);
                                  setOfferTitle('');
                                  setShowCustomerAddress(false);
                                  setRequireDeposit(false);
                                  setSaveCardOnDeposit(false);
                                  setOfferDepositPercentage('');
                                  setSelectedDepositPercentage(null);
                                  setSelectedServices([]);
                                  setSelectedProducts([]);
                                  setProductPhotos({});
                                  setCurrentVehicle(null);
                                  setCurrentOffer(null);
                                }}
                                className={`${isMobile ? 'px-3 py-2' : 'px-4 py-2'} text-sm font-medium text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 border border-gray-300 rounded-xl transition-all touch-manipulation`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Cancel
                              </button>
                              <button
                                type="button"
                                onClick={handleSaveOffer}
                                disabled={!offerTitle || selectedServices.length === 0}
                                className={`${isMobile ? 'px-3 py-2' : 'px-4 py-2'} text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                              >
                                Save Offer
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>,
                    document.body
                  )}
                </div>
              )}

            </div>
          </form>

          {/* Fixed Footer with Submit Button */}
          <div className="border-t border-gray-200 bg-white" style={{
            paddingTop: isMobile ? '12px' : '16px',
            paddingBottom: isMobile ? 'calc(12px + env(safe-area-inset-bottom, 0px))' : '16px',
            paddingLeft: isMobile ? '16px' : '24px',
            paddingRight: isMobile ? '16px' : '24px'
          }}>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                handleSubmit(e as any);
              }}
              disabled={
                loading || isOfferModalOpen || offers.length === 0 || offersNeedVehicles ||
                (templateMode ? !templateName.trim() : (!title || !isCustomerValid))
              }
              className={`w-full ${isMobile ? 'px-4 py-3 text-[15px]' : 'px-6 py-3.5 text-base'} font-semibold text-white rounded-xl transition-all duration-150 active:scale-[0.98] touch-manipulation shadow-sm ${
                (templateMode
                  ? (templateName.trim() && !isOfferModalOpen && offers.length > 0)
                  : (title && isCustomerValid && !isOfferModalOpen && offers.length > 0 && !offersNeedVehicles))
                  ? 'bg-blue-600 hover:bg-blue-700 active:bg-blue-800' 
                  : 'bg-gray-300 cursor-not-allowed'
              } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin mr-3"></div>
                  {templateMode
                    ? 'Saving Template...'
                    : (initialProposal ? 'Updating Proposal...' : 'Creating Proposal...')}
                </div>
              ) : (
                templateMode
                  ? (editTemplate ? 'Update Template' : 'Save Template')
                  : (initialProposal ? 'Update and review' : 'Create and review')
              )}
            </button>
            
            {!templateMode && showValidationMessages && !isCustomerValid && (
              <p className="mt-2 text-xs text-center text-gray-500 font-medium">Please select a customer</p>
            )}
            {templateMode && showValidationMessages && !templateName.trim() && (
              <p className="mt-2 text-xs text-center text-gray-500 font-medium">Please enter a template name</p>
            )}
            {showValidationMessages && (templateMode ? templateName.trim() : isCustomerValid) && (isOfferModalOpen || offers.length === 0) && (
              <p className="mt-2 text-xs text-center text-gray-500 font-medium">Please add at least one offer</p>
            )}
            {offersNeedVehicles && (
              <p className="mt-2 text-xs text-center text-amber-600 font-medium">Please assign a vehicle to all offers before creating</p>
            )}
          </div>
        </div>
      </div>

      <SelectCustomerModal
        isOpen={isSelectCustomerModalOpen}
        onClose={() => setIsSelectCustomerModalOpen(false)}
        onSelect={async (customer) => {
          let resolvedCustomer = customer;

          // Fetch full customer with vehicles
          if (customer.id) {
            try {
              resolvedCustomer = await getCustomer(customer.id);
            } catch (error) {
              console.error('Error loading customer with vehicles:', error);
            }
          }

          setSelectedCustomer(resolvedCustomer);
          await tryAutoLinkOpportunityForCustomer(resolvedCustomer);
          setIsSelectCustomerModalOpen(false);
        }}
      />

      <EditCustomerModal
        isOpen={isEditCustomerModalOpen}
        onClose={() => setIsEditCustomerModalOpen(false)}
        onSuccess={(updatedCustomer) => {
          setSelectedCustomer(updatedCustomer);
          setIsEditCustomerModalOpen(false);
        }}
        customer={selectedCustomer}
      />

      <SelectVehicleModal
        isOpen={isSelectVehicleModalOpen}
        onClose={() => setIsSelectVehicleModalOpen(false)}
        onSelect={(vehicle) => {
          setCurrentVehicle(vehicle);
          setIsSelectServiceModalOpen(true);
          setIsSelectVehicleModalOpen(false); 
        }}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={selectedServices.map(s => ({
          id: s.vehicleId,
          make: s.vehicleName.split(' ')[1] || '',
          model: s.vehicleName.split(' ')[2] || '',
          year: parseInt(s.vehicleName.split(' ')[0]) || new Date().getFullYear()
        })) as any}
        onAddVehicle={() => {
          setIsSelectVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      {/* Vehicle picker for assigning vehicles to template offers */}
      <SelectVehicleModal
        isOpen={isAssignVehicleModalOpen}
        onClose={() => {
          setIsAssignVehicleModalOpen(false);
          setAssignVehicleOfferIndex(null);
        }}
        onSelect={(vehicle) => {
          if (assignVehicleOfferIndex !== null) {
            handleAssignVehicleToOffer(vehicle, assignVehicleOfferIndex);
          }
        }}
        vehicles={selectedCustomer?.vehicles || []}
        selectedVehicles={[]}
        onAddVehicle={() => {
          setIsAssignVehicleModalOpen(false);
          setIsCreateVehicleModalOpen(true);
        }}
      />

      <SelectServiceModal
        isOpen={isSelectServiceModalOpen}
        onClose={() => setIsSelectServiceModalOpen(false)}
        onAdd={async (service, priceOption) => {
          // Check industry - for Home Services / template mode, vehicle is optional
          const isHomeServices = selectedBusiness?.industry === 'Home Services' || templateMode;
          
          if (!isHomeServices && !currentVehicle) {
            toast({
              title: 'No vehicle selected',
              description: 'Please select a vehicle first',
              variant: 'destructive'
            });
            return;
          }
          
          const isSelectable = localStorage.getItem('adding_selectable_service') === 'true';
          handleAddService(service, priceOption, isSelectable);
          localStorage.removeItem('adding_selectable_service');
        }}
        vehicleId={currentVehicle?.id}
      />

      <SelectInventoryItemModal
        isOpen={isSelectInventoryItemModalOpen}
        onClose={() => setIsSelectInventoryItemModalOpen(false)}
        onAdd={handleAddProduct}
        currentBookingProducts={selectedProducts.map(p => ({
          product: {
            id: p.id,
            name: p.name,
            quantity: p.quantity
          }
        }))}
      />

      <CreateVehicleModal
        isOpen={isCreateVehicleModalOpen}
        onClose={() => setIsCreateVehicleModalOpen(false)}
        onSuccess={handleVehicleCreated}
        customerId={selectedCustomer?.id || ''}
      />

      {/* Link Proposal Modal */}
      {isLinkModalOpen && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{ zIndex: baseZ + 10 }}
          onClick={() => {
            setIsLinkModalOpen(false);
            setLinkModalView('options');
            setProjectSearchQuery('');
            setOpportunitySearchQuery('');
          }}
        >
          <div 
            className="fixed inset-y-0 right-0 w-full md:w-[600px] bg-white shadow-2xl transform transition-transform duration-200 translate-x-0"
            onClick={e => e.stopPropagation()}
            style={{
              top: 'env(safe-area-inset-top, 0px)',
              bottom: 'env(safe-area-inset-bottom, 0px)',
              height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            }}
          >
            <div className="h-full flex flex-col">
              {/* Header */}
              <div className="bg-gray-50 border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {linkModalView === 'options' ? 'Link Proposal' : linkModalView === 'projects' ? 'Select Project' : 'Select Opportunity'}
                </h3>
                <button
                  onClick={() => {
                    setIsLinkModalOpen(false);
                    setLinkModalView('options');
                    setProjectSearchQuery('');
                    setOpportunitySearchQuery('');
                  }}
                  className="h-8 w-8 flex items-center justify-center text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 active:bg-gray-200 active:scale-95 transition-all duration-150"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {linkModalView === 'options' ? (
                  <div className="space-y-3">
                    {/* Projects Option */}
                    {isProjectsAppInstalled && (
                      <button
                        type="button"
                        onClick={async () => {
                          setLinkModalView('projects');
                          await fetchProjects();
                        }}
                        className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="h-10 w-10 rounded-lg bg-blue-100 flex items-center justify-center">
                            <FileText className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <div className="text-sm font-semibold text-gray-900">Projects</div>
                            <div className="text-xs text-gray-500">Link to a project</div>
                          </div>
                          {selectedProjectId && (
                            <div className="text-xs text-blue-600 font-medium">Selected</div>
                          )}
                        </div>
                      </button>
                    )}
                    {/* Opportunities Option */}
                    <button
                      type="button"
                      onClick={async () => {
                        setLinkModalView('opportunities');
                        await fetchOpportunities();
                      }}
                      className="w-full px-4 py-3 text-left bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 active:bg-gray-100 transition-all duration-150"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="h-10 w-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <Users className="h-5 w-5 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-gray-900">Opportunities</div>
                          <div className="text-xs text-gray-500">Link to an opportunity (lead)</div>
                        </div>
                        {selectedOpportunityId && (
                          <div className="text-xs text-blue-600 font-medium">Selected</div>
                        )}
                      </div>
                    </button>
                  </div>
                ) : linkModalView === 'projects' ? (
                  <div className="space-y-4">
                    {/* Back Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setLinkModalView('options');
                        setProjectSearchQuery('');
                      }}
                      className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                    >
                      <X className="h-4 w-4 mr-1 rotate-45" />
                      Back
                    </button>

                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={projectSearchQuery}
                        onChange={(e) => setProjectSearchQuery(e.target.value)}
                        placeholder="Search projects..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Projects List */}
                    {isLoadingProjects ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading projects...</div>
                    ) : filteredProjects.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {projectSearchQuery ? 'No projects found' : 'No projects available'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredProjects.map((project) => (
                          <button
                            key={project.id}
                            type="button"
                            onClick={() => {
                              setSelectedProjectId(project.id);
                              setSelectedProjectTitle(project.title);
                              setSelectedProjectCustomerId(project.customer_id || null);
                              setIsLinkModalOpen(false);
                              setLinkModalView('options');
                              setProjectSearchQuery('');
                            }}
                            className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                              selectedProjectId === project.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {project.title}
                                </div>
                                <div className="text-xs text-gray-500 mt-0.5 capitalize">
                                  {project.status}
                                </div>
                              </div>
                              {selectedProjectId === project.id && (
                                <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                  <X className="h-3 w-3 text-white rotate-45" />
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    {/* Back Button */}
                    <button
                      type="button"
                      onClick={() => {
                        setLinkModalView('options');
                        setOpportunitySearchQuery('');
                      }}
                      className="flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
                    >
                      <X className="h-4 w-4 mr-1 rotate-45" />
                      Back
                    </button>

                    {/* Search Input */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        value={opportunitySearchQuery}
                        onChange={(e) => setOpportunitySearchQuery(e.target.value)}
                        placeholder="Search opportunities..."
                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                      />
                    </div>

                    {/* Opportunities List */}
                    {isLoadingOpportunities ? (
                      <div className="text-center py-8 text-gray-500 text-sm">Loading opportunities...</div>
                    ) : filteredOpportunities.length === 0 ? (
                      <div className="text-center py-8 text-gray-500 text-sm">
                        {opportunitySearchQuery ? 'No opportunities found' : 'No opportunities available'}
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {filteredOpportunities.map((opportunity) => (
                          <button
                            key={opportunity.id}
                            type="button"
                            onClick={() => {
                              setSelectedOpportunityId(opportunity.id);
                              setSelectedOpportunityTitle(opportunity.title);
                              setSelectedOpportunityCustomerId(opportunity.customer_id);
                              setIsLinkModalOpen(false);
                              setLinkModalView('options');
                              setOpportunitySearchQuery('');
                            }}
                            className={`w-full px-4 py-3 text-left bg-white border rounded-lg hover:bg-gray-50 transition-all duration-150 ${
                              selectedOpportunityId === opportunity.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-300 hover:border-gray-400'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900 truncate">
                                  {opportunity.title}
                                </div>
                              </div>
                              {selectedOpportunityId === opportunity.id && (
                                <div className="ml-3 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                                  <X className="h-3 w-3 text-white rotate-45" />
                                </div>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Customer Conflict Warning Modal */}
      {showCustomerConflictModal && createPortal(
        <div 
          className="fixed inset-0 transition-opacity duration-200 bg-black/50"
          style={{ zIndex: baseZ + 15 }}
          onClick={() => setShowCustomerConflictModal(false)}
        >
          <div 
            className="fixed inset-0 flex items-center justify-center p-4"
            style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}
            onClick={e => e.stopPropagation()}
          >
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Customer Already Exists</h3>
              </div>

              <div className="px-6 py-4">
                <p className="text-sm text-gray-700 mb-2">
                  Customer already exists for selected project.
                </p>
                <p className="text-sm text-gray-600">
                  This will remove <span className="font-semibold">{conflictProjectCustomerName}</span> and add <span className="font-semibold">{selectedCustomer?.first_name} {selectedCustomer?.last_name}</span> to project.
                </p>
              </div>

              <div className="px-6 py-4 bg-gray-50 rounded-b-lg flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCustomerConflictModal(false);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    setShowCustomerConflictModal(false);
                    await createProposalWithProject();
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700"
                >
                  Proceed
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      <GoogleMeasurementModal
        isOpen={isMeasurementModalOpen}
        onClose={() => { setIsMeasurementModalOpen(false); setEditingMeasurementIndex(null); }}
        onSave={(result) => {
          if (editingMeasurementIndex !== null) {
            setMeasurements(prev => prev.map((m, i) => i === editingMeasurementIndex ? result : m));
          } else {
            setMeasurements(prev => [...prev, result]);
          }
          setIsMeasurementModalOpen(false);
          setEditingMeasurementIndex(null);
        }}
        initialMeasurement={editingMeasurementIndex !== null ? measurements[editingMeasurementIndex] : null}
        customerAddress={selectedCustomer?.address}
      />

      {/* Proposal Templates Modal (only in normal proposal mode) */}
      {!templateMode && (
        <ProposalTemplatesModal
          isOpen={isTemplatesModalOpen}
          onClose={() => setIsTemplatesModalOpen(false)}
          refreshKey={templatesRefreshKey}
          onSelectTemplate={(template) => {
            applyTemplate(template);
            setIsTemplatesModalOpen(false);
          }}
          onCreateTemplate={() => {
            setEditingTemplate(null);
            setIsTemplateCreateOpen(true);
          }}
          onEditTemplate={(template) => {
            setEditingTemplate(template);
            setIsTemplateCreateOpen(true);
          }}
        />
      )}

      {/* Template-mode CreateProposalModal (for creating/editing templates) */}
      {!templateMode && (
        <CreateProposalModal
          isOpen={isTemplateCreateOpen}
          onClose={() => {
            setIsTemplateCreateOpen(false);
            setEditingTemplate(null);
          }}
          templateMode
          editTemplate={editingTemplate}
          onTemplateSaved={(saved) => {
            setIsTemplateCreateOpen(false);
            setEditingTemplate(null);
            setTemplatesRefreshKey(k => k + 1);
            toast({
              title: editingTemplate ? 'Template updated' : 'Template created',
              description: `"${saved.name}" has been saved.`,
            });
          }}
        />
      )}
    </div>
  );

  // 🚀 FIX: Use React Portal to render modal outside parent DOM tree
  // This prevents PWA stacking context issues and ensures consistent behavior
  // whether triggered from FAB menu or Proposals page button
  return createPortal(
    <>
      {modalContent}

      {/* Shared hidden file input (camera roll / take photo) */}
      <input ref={addFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={onAddFileInputChange} />

      {/* Action sheet: choose linked vs new */}
      {addPhotoTarget && (
        <div className="fixed inset-0 flex items-end sm:items-center justify-center" style={{ zIndex: baseZ + 40 }} onClick={() => setAddPhotoTarget(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative w-full sm:max-w-sm p-3 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="rounded-2xl overflow-hidden bg-white shadow-xl divide-y divide-gray-200">
              {fieldQuotePhotoGroups.length > 0 && (
                <button onClick={openLinkedPicker} className="w-full px-4 py-3.5 text-[16px] text-blue-600 font-medium flex items-center justify-center gap-2 active:bg-gray-50">
                  <ImageIcon className="h-5 w-5" /> Use field quote photos
                </button>
              )}
              {bookingPhotos.length > 0 && (
                <button onClick={openBookingPicker} className="w-full px-4 py-3.5 text-[16px] text-blue-600 font-medium flex items-center justify-center gap-2 active:bg-gray-50">
                  <ImageIcon className="h-5 w-5" /> Use booking photos
                </button>
              )}
              <button onClick={chooseNewPhoto} className="w-full px-4 py-3.5 text-[16px] text-blue-600 font-medium flex items-center justify-center gap-2 active:bg-gray-50">
                <Plus className="h-5 w-5" /> New photo
              </button>
            </div>
            <button onClick={() => setAddPhotoTarget(null)} className="w-full rounded-2xl bg-white px-4 py-3.5 text-[16px] font-semibold text-gray-900 shadow-xl active:bg-gray-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Linked field-quote photo picker */}
      {linkedPickerTarget && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: baseZ + 50 }} onClick={() => setLinkedPickerTarget(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <button onClick={() => setLinkedPickerTarget(null)} className="text-[15px] text-blue-600">Cancel</button>
              <span className="text-[15px] font-semibold text-gray-900">Field quote photos</span>
              <button onClick={confirmLinkedPhotos} disabled={linkedSelected.size === 0} className="text-[15px] text-blue-600 font-semibold disabled:opacity-40">
                Add{linkedSelected.size > 0 ? ` (${linkedSelected.size})` : ''}
              </button>
            </div>
            <div className="p-3 overflow-y-auto space-y-4">
              {fieldQuotePhotoGroups.map((group) => (
                <div key={group.fieldQuoteId}>
                  <div className="flex items-baseline gap-2 mb-1.5">
                    <span className="text-[13px] font-semibold text-gray-900 truncate">{group.label}</span>
                    {group.subLabel && <span className="text-[11px] text-gray-400 shrink-0">{group.subLabel}</span>}
                  </div>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                    {group.photos.map((p) => {
                      const sel = linkedSelected.has(p.id);
                      return (
                        <button
                          key={p.id}
                          type="button"
                          onClick={() =>
                            setLinkedSelected((prev) => {
                              const n = new Set(prev);
                              if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                              return n;
                            })
                          }
                          className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel ? 'border-blue-600' : 'border-transparent'}`}
                        >
                          <img src={fieldQuotePhotoUrl(p.file_path)} alt="" className="w-full h-full object-cover bg-gray-100" />
                          {sel && (
                            <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center shadow">
                              <Check className="h-3 w-3" />
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Linked booking photo picker */}
      {bookingPickerTarget && (
        <div className="fixed inset-0 flex items-center justify-center p-4" style={{ zIndex: baseZ + 50 }} onClick={() => setBookingPickerTarget(null)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative w-full max-w-lg max-h-[80vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <button onClick={() => setBookingPickerTarget(null)} className="text-[15px] text-blue-600">Cancel</button>
              <span className="text-[15px] font-semibold text-gray-900">Booking photos</span>
              <button onClick={confirmBookingPhotos} disabled={bookingSelected.size === 0} className="text-[15px] text-blue-600 font-semibold disabled:opacity-40">
                Add{bookingSelected.size > 0 ? ` (${bookingSelected.size})` : ''}
              </button>
            </div>
            <div className="p-3 overflow-y-auto">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {bookingPhotos.map((p) => {
                  const sel = bookingSelected.has(p.id);
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() =>
                        setBookingSelected((prev) => {
                          const n = new Set(prev);
                          if (n.has(p.id)) n.delete(p.id); else n.add(p.id);
                          return n;
                        })
                      }
                      className={`relative aspect-square rounded-lg overflow-hidden border-2 ${sel ? 'border-blue-600' : 'border-transparent'}`}
                    >
                      <img src={p.url} alt="" className="w-full h-full object-cover bg-gray-100" />
                      {sel && (
                        <span className="absolute top-1 right-1 h-5 w-5 rounded-full bg-blue-600 text-white flex items-center justify-center shadow">
                          <Check className="h-3 w-3" />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}